import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Bot, Sparkles, Send, ArrowLeft, Bug } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';
import { sanitizeBasicHtml } from '../../utils/sanitize';

const BankAgentChat = () => {
  console.log('🚀 [VERSION] BankAgentChat v2025.01.15.1 - TDS Partial Payment Fix');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const bottomRef = useRef(null);
  const transactionInitializedRef = useRef(false);
  const jeDataFetchedRef = useRef(false);

  // Debug state for admin users to view JSON payloads
  const [expandedDebugId, setExpandedDebugId] = useState(null);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Get transaction data from URL
  const transactionData = searchParams.get('transaction');
  const parentWorkflowId = searchParams.get('parentWorkflow');
  const parentRunId = searchParams.get('parentRun');
  const urlChildRunId = searchParams.get('childRun');

  // State
  const [transaction, setTransaction] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [childWorkflowId, setChildWorkflowId] = useState(null);
  const [childRunId, setChildRunId] = useState(urlChildRunId || null);
  const [assistantId, setAssistantId] = useState(null);
  const [error, setError] = useState(null);

  // Manual modification state
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [enableReallocation, setEnableReallocation] = useState(false);
  const [allocationAmounts, setAllocationAmounts] = useState({});
  const [isUpdatingEntity, setIsUpdatingEntity] = useState(false);
  const [isUpdatingAllocation, setIsUpdatingAllocation] = useState(false);
  const [isTransferringToJE, setIsTransferringToJE] = useState(false);
  const [transactionDataSent, setTransactionDataSent] = useState(false);

  // All entities for dropdown (fetched from NetSuite when not matched)
  const [allEntities, setAllEntities] = useState([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [entitySearchTerm, setEntitySearchTerm] = useState('');


  // JE Agent specific state
  const [jeLines, setJeLines] = useState([]);
  const [accountGroups, setAccountGroups] = useState({});
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [selectedJeLines, setSelectedJeLines] = useState({});
  const [jeLineUpdates, setJeLineUpdates] = useState({}); // {line_index: new_account_id}
  const [jeMassUpdateEnabled, setJeMassUpdateEnabled] = useState(false);
  const [jeMassUpdateAccountId, setJeMassUpdateAccountId] = useState('');
  const [isApplyingJeUpdates, setIsApplyingJeUpdates] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [netsuiteAccountId, setNetsuiteAccountId] = useState('');
  const [jeLineMemos, setJeLineMemos] = useState({}); // {line_index: memo_value}
  const [jeLineDepartments, setJeLineDepartments] = useState({}); // {line_index: department_id}
  const [jeLineClasses, setJeLineClasses] = useState({}); // {line_index: class_id}
  const [jeLineLocations, setJeLineLocations] = useState({}); // {line_index: location_id}
  const [departmentList, setDepartmentList] = useState([]); // [{id, name}, ...]
  const [classList, setClassList] = useState([]); // [{id, name}, ...]
  const [locationList, setLocationList] = useState([]); // [{id, name}, ...]
  const [isLoadingClassesLocations, setIsLoadingClassesLocations] = useState(false);

  // Customer and Vendor dropdowns for JE lines
  const [jeLineCustomers, setJeLineCustomers] = useState({}); // {line_index: customer_id}
  const [jeLineVendors, setJeLineVendors] = useState({}); // {line_index: vendor_id}
  const [customerList, setCustomerList] = useState([]); // [{value, label}, ...]
  const [vendorList, setVendorList] = useState([]); // [{value, label}, ...]
  const [isLoadingCustomersVendors, setIsLoadingCustomersVendors] = useState(false);

  // Fetch payment details state (for timeout recovery)
  const [isFetchingPayment, setIsFetchingPayment] = useState(false);

  // Track TDS payment creation (to hide Create Payment button)
  const [tdsPaymentCreated, setTdsPaymentCreated] = useState(null); // {payment_id, payment_link}

  // Track which action button is loading ('create' | 'check' | null)
  const [loadingAction, setLoadingAction] = useState(null);

  // TDS percentage state (for customer payments)
  const [tdsPercentage, setTdsPercentage] = useState('');
  const [tdsNotApplicable, setTdsNotApplicable] = useState(false);
  const [tdsAmount, setTdsAmount] = useState(0);
  const [tdsAllocation, setTdsAllocation] = useState(null); // TDS allocation calculation result

  // Build NetSuite URL based on account_id
  const getNetSuiteUrl = (recordType, recordId) => {
    // Replace underscores with hyphens for NetSuite URL format
    const formattedAccountId = netsuiteAccountId ? netsuiteAccountId.replace(/_/g, '-') : '';
    const baseUrl = formattedAccountId
      ? `https://${formattedAccountId}.app.netsuite.com`
      : 'https://system.netsuite.com';

    const recordPaths = {
      'custpymt': 'app/accounting/transactions/custpymt.nl',
      'vendpymt': 'app/accounting/transactions/vendpymt.nl',
      'vprep': 'app/accounting/transactions/vprep.nl',
      'journal': 'app/accounting/transactions/journal.nl'
    };

    const path = recordPaths[recordType] || `app/accounting/transactions/${recordType}.nl`;
    return `${baseUrl}/${path}?id=${recordId}`;
  };

  // Format message content - hide JE Lines JSON and show a clean message
  const formatMessageContent = (content) => {
    if (!content) return content;

    // Handle case where content is already an object (not a string)
    if (typeof content === 'object' && content !== null) {
      if (content.user_message) {
        return content.user_message;
      }
      // Fallback: stringify
      content = JSON.stringify(content);
    }

    // Check if it's JSON with user_message field
    const trimmed = String(content).trim();
    if (trimmed.startsWith('{')) {
      try {
        // First try stripping JS-style comments (AI sometimes adds // comments in JSON)
        const cleaned = trimmed.replace(/^\s*\/\/.*$/gm, '');
        const parsed = JSON.parse(cleaned);
        if (parsed.user_message) {
          return parsed.user_message;
        }
      } catch (e) {
        // JSON.parse failed even after comment stripping - use regex fallback
        // Extract user_message directly from the malformed JSON string
        const userMsgMatch = trimmed.match(/"user_message"\s*:\s*"((?:[^"\\]|\\.)*)"/s);
        if (userMsgMatch) {
          try {
            // Parse the extracted string value to unescape \n, \", etc.
            return JSON.parse('"' + userMsgMatch[1] + '"');
          } catch (e2) {
            return userMsgMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
          }
        }
      }
    }

    // Check if message contains "JE Lines with selected accounts:" and JSON
    if (content.includes('JE Lines with selected accounts:')) {
      // Extract just the first line/action before the JSON
      const parts = content.split('\n\nJE Lines with selected accounts:');
      if (parts.length > 0 && parts[0]) {
        // Return just the action message like "create journal entry"
        return parts[0].trim() + '\n\n(JE details applied)';
      }
    }

    // Check for other markers
    const markers = ['\n\nTransaction data:', '\n\n[CRITICAL:', '\n\nbankdata:'];
    for (const marker of markers) {
      const idx = content.indexOf(marker);
      if (idx > -1) {
        return content.substring(0, idx).trim();
      }
    }

    return content;
  };

  // Helper to normalize transaction data (used by both URL and API fetch)
  const normalizeTransaction = (parsed) => {
    return {
      Amount: parsed.amount || parsed.Amount || '',
      bank: parsed.bank || '',
      customername: parsed.customername || '',
      vendorname: parsed.vendorname || '',
      description: parsed.description || '',
      invoicelist: parsed.invoicelist || [],
      billlist: parsed.billlist || [],
      invoicenumber: parsed.invoicenumber || '',
      ns_pay_doc: parsed.ns_pay_doc || [],
      ns_payment_id: parsed.ns_payment_id || [],
      ns_billpayment_id: parsed.ns_billpayment_id || [],
      ns_billpay_doc: parsed.ns_billpay_doc || [],
      ns_je_id: parsed.ns_je_id || parsed.ns_JE_id || [],
      ns_je_doc: parsed.ns_je_doc || parsed.ns_JE_doc || [],
      nscustomerinternalid: parsed.nscustomerinternalid || '',
      nscustomername: parsed.nscustomername || '',
      nsvendorinternalid: parsed.nsvendorinternalid || '',
      nsvendorname: parsed.nsvendorname || '',
      ns_tds_percentage: parsed.ns_tds_percentage || '',
      nsinvoiceamount: parsed.nsinvoiceamount || [],
      nsinvoicedocnum: parsed.nsinvoicedocnum || [],
      nsinvoiceinternalid: parsed.nsinvoiceinternalid || [],
      nsbillamount: parsed.nsbillamount || [],
      nsbilldocnum: parsed.nsbilldocnum || [],
      nsbillinternalid: parsed.nsbillinternalid || [],
      similarcustomer: parsed.similarcustomer || [],
      similarvendor: parsed.similarvendor || [],
      trandate: parsed.date || parsed.trandate || '',
      transactiontype: parsed.transactiontype || 'credit',
      agent_type: parsed.agent_type || 'bank_agent',
      transaction_index: parsed.transaction_index || 0,
      creditsarray: parsed.creditsarray || [],
      _original: parsed
    };
  };

  // Initialize transaction and set welcome message
  const initializeTransaction = (parsed, source) => {
    const normalizedTransaction = normalizeTransaction(parsed);

    // Initialize TDS percentage from transaction data (for customer payments)
    console.log(`🔢 [TDS DEBUG] Parsed transaction from ${source}:`, {
      ns_tds_percentage: parsed.ns_tds_percentage,
      agent_type: parsed.agent_type,
      transactiontype: parsed.transactiontype,
      customername: parsed.customername
    });
    if (parsed.ns_tds_percentage) {
      setTdsPercentage(parsed.ns_tds_percentage);
      setTdsNotApplicable(false);
      console.log('🔢 [TDS DEBUG] Set tdsPercentage state to:', parsed.ns_tds_percentage);
    } else {
      setTdsNotApplicable(true);
    }

    // Mark as initialized BEFORE setting state
    transactionInitializedRef.current = true;
    console.log(`🔴 [useEffect] Setting initial transaction from ${source}`);

    setTransaction(normalizedTransaction);

    const welcomeMessage = {
      id: 'welcome',
      type: 'assistant',
      content: `Hello! I'm here to help you process this ${getTransactionTypeName(parsed.agent_type)} transaction.\n\n**Transaction Details:**\n- **Amount:** ${normalizedTransaction.Amount}\n- **Date:** ${normalizedTransaction.trandate}\n- **Description:** ${normalizedTransaction.description}\n- **${parsed.agent_type === 'bill_agent' ? 'Vendor' : 'Customer'}:** ${normalizedTransaction.customername || normalizedTransaction.vendorname || 'Not identified'}\n\nPlease send a message to start the payment processing workflow.`,
      timestamp: new Date().toISOString()
    };

    setMessages([welcomeMessage]);
  };

  // Parse transaction data on mount - only runs ONCE
  // Priority: 1) Fetch from API using childRunId, 2) Parse from URL transactionData
  useEffect(() => {
    // Skip if already initialized (prevents overwriting on URL changes)
    if (transactionInitializedRef.current) {
      console.log('🔴 [URL useEffect] Already initialized, skipping');
      return;
    }

    const loadTransaction = async () => {
      // Priority 1: If we have childRunId, fetch transaction from API (simpler URL)
      if (urlChildRunId && token) {
        try {
          console.log('🔄 [URL useEffect] Fetching transaction from API using childRunId:', urlChildRunId);
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
          const response = await fetch(`${API_BASE_URL}/api/bank/v2/get-transaction/${urlChildRunId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.transaction) {
              console.log('✅ [URL useEffect] Got transaction from API:', data.transaction);
              if (data.netsuite_account_id) {
                setNetsuiteAccountId(data.netsuite_account_id);
              }
              initializeTransaction(data.transaction, 'API');
              return; // Success - don't fall back to URL
            }
          }
          console.log('⚠️ [URL useEffect] API fetch failed, falling back to URL params');
        } catch (err) {
          console.error('⚠️ [URL useEffect] Error fetching from API:', err);
        }
      }

      // Priority 2: Fall back to URL transactionData
      if (transactionData) {
        try {
          const parsed = JSON.parse(decodeURIComponent(transactionData));
          console.log('🔢 [TDS DEBUG] RAW parsed from URL - all keys:', Object.keys(parsed));
          initializeTransaction(parsed, 'URL');
        } catch (err) {
          console.error('Error parsing transaction data:', err);
          setError('Invalid transaction data');
        }
      } else if (!urlChildRunId) {
        setError('No transaction data provided');
      }
    };

    loadTransaction();
  }, [transactionData, urlChildRunId, token]);

  // Auto scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Calculate allocation when invoice data changes
  // Works for both TDS applicable and non-TDS cases
  // Also recalculates when manual_allocations change
  useEffect(() => {
    const invoicelist = transaction?.invoicelist || [];
    const manualAllocations = transaction?.manual_allocations || [];
    const paymentAmount = Math.abs(parseFloat(transaction?.Amount) || 0);

    // Set TDS rate to 0 if TDS is not applicable
    const tdsRate = (tdsNotApplicable || !tdsPercentage || parseFloat(tdsPercentage) === 0)
      ? 0
      : parseFloat(tdsPercentage) / 100;

    if (invoicelist.length > 0 && paymentAmount > 0) {
      let totalTds = 0;
      const allocations = [];

      // Check if manual allocations exist - if so, use them instead of auto-calculating
      if (manualAllocations.length > 0) {
        console.log('📊 [TDS CALC] Using MANUAL allocations:', manualAllocations.length);

        // Build lookup map from invoicelist for invoice details
        const invoiceMap = {};
        for (const inv of invoicelist) {
          const invNumber = inv.InvoiceNumber || inv.invoicenumber;
          const invId = String(inv.internalid);
          invoiceMap[invNumber] = inv;
          invoiceMap[invId] = inv;
        }

        for (const manualAlloc of manualAllocations) {
          const allocAmount = parseFloat(manualAlloc.amount) || 0;
          if (allocAmount <= 0) continue;

          // Find matching invoice (manual_allocations uses invoice_id which could be docnum or internalid)
          const inv = invoiceMap[manualAlloc.invoice_id] || invoiceMap[String(manualAlloc.invoice_id)];

          if (!inv) {
            console.warn('📊 [TDS CALC] Could not find invoice for manual allocation:', manualAlloc.invoice_id);
            continue;
          }

          const totpayamount = Math.abs(parseFloat(inv.totpayamount) || 0);
          const taxtotal = Math.abs(parseFloat(inv.taxtotal) || 0);
          const originalTotal = totpayamount + taxtotal;
          const invoiceDue = Math.abs(parseFloat(inv.invoice_total) || 0);

          // User enters CASH amount to apply to invoice
          // TDS is calculated on the item portion and added as JE credit
          // Formula:
          //   - Cash = allocAmount (what user entered)
          //   - Item portion of cash = cash × (totpayamount / originalTotal)
          //   - TDS = itemPortion × TDS%
          //   - Amount Applied to Invoice = Cash (applied directly)
          //   - TDS JE Credit = calculated separately and applied as credit

          const cashPayment = allocAmount; // User entered this as cash to apply

          let itemPortion, taxPortion, tdsForInvoice;
          if (originalTotal > 0) {
            // Calculate proportional item/tax amounts for this cash payment
            const cashRatio = cashPayment / originalTotal;
            itemPortion = totpayamount * cashRatio;
            taxPortion = taxtotal * cashRatio;

            // Check if invoice has previous payments (Due < Original Total)
            const hasPreviousPayments = invoiceDue < (originalTotal - 0.01);
            const maxTds = totpayamount * tdsRate;  // Maximum TDS for this invoice

            if (hasPreviousPayments && tdsRate > 0) {
              // Calculate remaining TDS capacity for partially paid invoice
              // Simple formula: invoice portion paid → item portion paid → TDS paid
              const invoicePortionPaid = originalTotal - invoiceDue;  // Amount already applied to invoice
              const itemPortionPaid = invoicePortionPaid * totpayamount / originalTotal;  // Item portion of that
              const tdsAlreadyPaid = itemPortionPaid * tdsRate;  // TDS on that item portion
              const remainingTdsCapacity = Math.max(0, maxTds - tdsAlreadyPaid);

              // If cash + remaining TDS closes the invoice, use all remaining TDS
              if (Math.abs((cashPayment + remainingTdsCapacity) - invoiceDue) < 1) {
                tdsForInvoice = remainingTdsCapacity;
              } else {
                // Use proportional TDS, capped at remaining capacity
                const proportionalTds = itemPortion * tdsRate;
                tdsForInvoice = Math.min(proportionalTds, remainingTdsCapacity);
              }
              console.log(`📊 [TDS CALC] Partial invoice ${inv.InvoiceNumber}: invoicePaid=${invoicePortionPaid.toFixed(2)}, itemPaid=${itemPortionPaid.toFixed(2)}, maxTds=${maxTds.toFixed(2)}, tdsAlreadyPaid=${tdsAlreadyPaid.toFixed(2)}, remainingTds=${remainingTdsCapacity.toFixed(2)}, tdsForInvoice=${tdsForInvoice.toFixed(2)}`);
            } else {
              // Fresh invoice - check if this is full payment or partial
              // Expected cash for full payment = item*(1-TDS%) + tax
              const expectedCashForFull = (totpayamount * (1 - tdsRate)) + taxtotal;

              if (Math.abs(cashPayment - expectedCashForFull) < 1 || cashPayment >= expectedCashForFull) {
                // Full payment - use full TDS
                tdsForInvoice = maxTds;
                // Recalculate item/tax portions for full payment
                itemPortion = totpayamount;
                taxPortion = taxtotal;
              } else {
                // Partial payment - calculate invoice portion covered by this cash amount
                // Formula: invoicePortionCovered = Cash × originalTotal / expectedCashForFull
                // Then TDS = itemPortionCovered × TDS_rate
                const invoicePortionCovered = (expectedCashForFull > 0)
                  ? Math.min(cashPayment * originalTotal / expectedCashForFull, invoiceDue)
                  : cashPayment;
                const itemPortionCovered = invoicePortionCovered * totpayamount / originalTotal;
                const taxPortionCovered = invoicePortionCovered * taxtotal / originalTotal;
                itemPortion = itemPortionCovered;
                taxPortion = taxPortionCovered;
                tdsForInvoice = itemPortionCovered * tdsRate;
                console.log(`📊 [TDS CALC] Partial payment: cash=${cashPayment}, expectedFull=${expectedCashForFull.toFixed(2)}, invoicePortion=${invoicePortionCovered.toFixed(2)}, itemPortion=${itemPortionCovered.toFixed(2)}, tds=${tdsForInvoice.toFixed(2)}`);
              }
            }
          } else {
            itemPortion = 0;
            taxPortion = 0;
            tdsForInvoice = 0;
          }
          // Total applied to invoice = cash + TDS credit
          const amountAppliedToInvoice = cashPayment + tdsForInvoice;

          allocations.push({
            invoice_number: inv.InvoiceNumber || inv.invoicenumber,
            invoice_id: inv.internalid,
            invoice_due: invoiceDue,
            original_total: originalTotal,
            totpayamount: totpayamount,  // Full invoice item amount (not covered portion)
            taxtotal: taxtotal,          // Full invoice tax amount (not covered portion)
            expected_payment: Math.round(cashPayment * 100) / 100,
            payment_allocated: Math.round(cashPayment * 100) / 100, // Cash payment = what user entered
            tds_amount: Math.round(tdsForInvoice * 100) / 100,
            amount_applied: Math.round(amountAppliedToInvoice * 100) / 100, // Invoice reduction = cash + TDS
            is_partial: amountAppliedToInvoice < invoiceDue,
            is_manual: true
          });

          totalTds += tdsForInvoice;
        }
      } else {
        // Auto-calculate based on due date (oldest first)
        console.log('📊 [TDS CALC] Using AUTO allocation by due date');

        let remainingPayment = paymentAmount;

        // Sort by due date (parse MM/DD/YYYY format correctly)
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date('9999-12-31');
          // Handle MM/DD/YYYY format
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            return new Date(parts[2], parts[0] - 1, parts[1]); // year, month (0-indexed), day
          }
          return new Date(dateStr);
        };
        const sortedInvoices = [...invoicelist].sort((a, b) => {
          const dateA = parseDate(a.duedate);
          const dateB = parseDate(b.duedate);
          return dateA - dateB;
        });

        for (const inv of sortedInvoices) {
          if (remainingPayment <= 0) break;

          const totpayamount = Math.abs(parseFloat(inv.totpayamount) || 0);
          const taxtotal = Math.abs(parseFloat(inv.taxtotal) || 0);
          const originalTotal = totpayamount + taxtotal;
          const invoiceDue = Math.abs(parseFloat(inv.invoice_total) || 0); // Actual due amount

          // Skip if no due amount
          if (invoiceDue <= 0) continue;

          // TDS is always calculated on totpayamount (item amount), not on invoice due
          // Use the actual totpayamount and taxtotal from the invoice
          const effectiveTotpayamount = totpayamount;
          const effectiveTaxtotal = taxtotal;

          // Check if invoice has previous payments (Due < Original Total)
          const hasPreviousPayments = invoiceDue < (originalTotal - 0.01);
          let remainingTdsCapacity = effectiveTotpayamount * tdsRate; // Default to max TDS

          if (hasPreviousPayments && tdsRate > 0) {
            // Calculate remaining TDS capacity for partially paid invoice
            // Simple formula: invoice portion paid → item portion paid → TDS paid
            const invoicePortionPaid = originalTotal - invoiceDue;  // Amount already applied to invoice
            const itemPortionPaid = invoicePortionPaid * effectiveTotpayamount / originalTotal;  // Item portion of that
            const tdsAlreadyPaid = itemPortionPaid * tdsRate;  // TDS on that item portion
            const maxTds = effectiveTotpayamount * tdsRate;
            remainingTdsCapacity = Math.max(0, maxTds - tdsAlreadyPaid);
            console.log(`📊 [TDS AUTO] Partial invoice ${inv.InvoiceNumber}: invoicePaid=${invoicePortionPaid.toFixed(2)}, itemPaid=${itemPortionPaid.toFixed(2)}, maxTds=${maxTds.toFixed(2)}, tdsAlreadyPaid=${tdsAlreadyPaid.toFixed(2)}, remainingTds=${remainingTdsCapacity.toFixed(2)}`);
          }

          // Expected payment = item amount after TDS + tax (using remaining TDS capacity)
          // Formula: Cash = (totpayamount - remainingTds) + taxtotal for full payment
          // But for partial invoice, use remaining capacity
          const effectiveExpectedCash = hasPreviousPayments
            ? (invoiceDue - remainingTdsCapacity)  // Cash needed to close partially paid invoice
            : (effectiveTotpayamount * (1 - tdsRate)) + effectiveTaxtotal;  // Fresh invoice

          if (effectiveExpectedCash <= 0) continue;

          let paymentForInvoice, tdsForInvoice, amountApplied, isPartial;

          if (remainingPayment >= effectiveExpectedCash) {
            // Full payment for this invoice
            tdsForInvoice = remainingTdsCapacity;  // Use all remaining TDS
            paymentForInvoice = invoiceDue - tdsForInvoice;  // Cash needed
            amountApplied = invoiceDue;
            isPartial = false;
          } else {
            // Partial payment - calculate invoice portion covered by this cash amount
            // Formula: P = Cash × total / (item × (1 - TDS_rate) + tax)
            // This ensures TDS is correctly calculated on the item portion
            paymentForInvoice = remainingPayment;

            // Calculate cash needed to fully pay this invoice (with TDS)
            const cashNeededForFull = (effectiveTotpayamount * (1 - tdsRate)) + effectiveTaxtotal;

            // Calculate invoice portion covered by this cash payment
            const invoicePortionCovered = (cashNeededForFull > 0)
              ? Math.min(paymentForInvoice * originalTotal / cashNeededForFull, invoiceDue)
              : paymentForInvoice;

            // Calculate item portion and TDS from the covered portion
            const itemPortionCovered = invoicePortionCovered * effectiveTotpayamount / originalTotal;
            const calculatedTds = itemPortionCovered * tdsRate;
            tdsForInvoice = Math.min(calculatedTds, remainingTdsCapacity);

            // Amount applied to invoice = cash + TDS credit
            amountApplied = paymentForInvoice + tdsForInvoice;
            isPartial = true;

            console.log(`📊 [TDS PARTIAL] Cash=${paymentForInvoice}, invoicePortion=${invoicePortionCovered.toFixed(2)}, itemPortion=${itemPortionCovered.toFixed(2)}, TDS=${tdsForInvoice.toFixed(2)}, Applied=${amountApplied.toFixed(2)}`);
          }

          allocations.push({
            invoice_number: inv.InvoiceNumber || inv.invoicenumber,
            invoice_id: inv.internalid,
            invoice_due: invoiceDue,
            original_total: originalTotal,
            totpayamount: effectiveTotpayamount,
            taxtotal: effectiveTaxtotal,
            expected_payment: Math.round(effectiveExpectedCash * 100) / 100,
            payment_allocated: Math.round(paymentForInvoice * 100) / 100,
            tds_amount: Math.round(tdsForInvoice * 100) / 100,
            amount_applied: Math.round(amountApplied * 100) / 100,
            is_partial: isPartial,
            is_manual: false
          });

          remainingPayment -= paymentForInvoice;
          totalTds += tdsForInvoice;
        }
      }

      setTdsAmount(Math.round(totalTds * 100) / 100);
      setTdsAllocation({
        allocations,
        total_tds: Math.round(totalTds * 100) / 100,
        total_payment: paymentAmount,
        tds_percentage: tdsRate * 100,  // Use calculated rate (0 for non-TDS)
        is_manual: manualAllocations.length > 0,
        tds_not_applicable: tdsNotApplicable || tdsRate === 0
      });

      console.log('📊 [ALLOC] Calculated - TDS:', totalTds, 'Allocations:', allocations.length, 'Manual:', manualAllocations.length > 0, 'TDS N/A:', tdsNotApplicable || tdsRate === 0);
    } else {
      setTdsAmount(0);
      setTdsAllocation(null);
    }
  }, [tdsPercentage, tdsNotApplicable, transaction?.invoicelist, transaction?.Amount, transaction?.manual_allocations]);

  // Auto-allocation for vendor bills (bill_agent) - NO TDS, includes bill credits
  useEffect(() => {
    // Only run for vendor payments (bill_agent)
    if (transaction?.agent_type !== 'bill_agent') return;

    const billlist = transaction?.billlist || [];
    const paymentAmount = Math.abs(parseFloat(transaction?.Amount) || 0);

    if (billlist.length > 0 && paymentAmount > 0) {
      console.log('📊 [VENDOR ALLOC] Processing billlist:', billlist.length, 'Payment:', paymentAmount);

      const allocations = [];

      // Separate credits (negative) and bills (positive)
      const credits = billlist.filter(b => parseFloat(b.bill_total) < 0);
      const bills = billlist.filter(b => parseFloat(b.bill_total) >= 0);

      console.log('📊 [VENDOR ALLOC] Bills:', bills.length, 'Credits:', credits.length);

      // 1) First add all credits (they reduce the payment)
      let totalCredits = 0;
      for (const credit of credits) {
        const creditAmount = parseFloat(credit.bill_total) || 0; // Keep negative
        const creditNum = credit.billnumber || credit.BillNumber;
        const creditInternalId = credit.internalid;

        totalCredits += Math.abs(creditAmount);

        allocations.push({
          invoice_number: creditNum,
          invoice_id: creditInternalId || creditNum,
          invoice_due: creditAmount, // Keep negative
          original_total: creditAmount,
          totpayamount: creditAmount,
          taxtotal: 0,
          expected_payment: creditAmount,
          payment_allocated: creditAmount, // Keep negative
          tds_amount: 0,
          amount_applied: creditAmount, // Keep negative
          is_partial: false,
          is_manual: false,
          is_credit: true
        });
      }

      // 2) Then allocate to bills (positive amounts)
      // Net amount to allocate to bills = paymentAmount + totalCredits (credits reduce what we need to pay)
      let remainingPayment = paymentAmount + totalCredits;

      console.log('📊 [VENDOR ALLOC] After credits, remaining to allocate:', remainingPayment);

      // Sort bills by due date (oldest first)
      const parseDate = (dateStr) => {
        if (!dateStr) return new Date('9999-12-31');
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          return new Date(parts[2], parts[0] - 1, parts[1]);
        }
        return new Date(dateStr);
      };
      const sortedBills = [...bills].sort((a, b) => parseDate(a.duedate) - parseDate(b.duedate));

      for (const bill of sortedBills) {
        if (remainingPayment <= 0) break;

        const billDue = parseFloat(bill.bill_total) || 0;
        if (billDue <= 0) continue;

        const paymentForBill = Math.min(remainingPayment, billDue);
        const isPartial = paymentForBill < billDue;

        const billNum = bill.billnumber || bill.BillNumber;
        const billInternalId = bill.internalid;

        allocations.push({
          invoice_number: billNum,
          invoice_id: billInternalId || billNum,
          invoice_due: billDue,
          original_total: billDue,
          totpayamount: billDue,
          taxtotal: 0,
          expected_payment: billDue,
          payment_allocated: Math.round(paymentForBill * 100) / 100,
          tds_amount: 0,
          amount_applied: Math.round(paymentForBill * 100) / 100,
          is_partial: isPartial,
          is_manual: false,
          is_credit: false
        });

        remainingPayment -= paymentForBill;
      }

      setTdsAllocation({
        allocations,
        total_tds: 0,
        total_payment: paymentAmount,
        total_credits: totalCredits,
        tds_percentage: 0,
        is_manual: false,
        tds_not_applicable: true
      });

      console.log('📊 [VENDOR ALLOC] Final allocations:', allocations.length, allocations);
    } else if (transaction?.agent_type === 'bill_agent') {
      // Reset allocation when no bills
      setTdsAllocation(null);
    }
  }, [transaction?.agent_type, transaction?.billlist, transaction?.Amount]);

  // Load existing run info and chat history when childRunId is available (for page reload persistence)
  useEffect(() => {
    const loadExistingSession = async () => {
      if (!childRunId || !token) return;

      try {
        // First load run info to restore workflow state
        const runInfoResponse = await fetch(`/api/bank/v2/workflows/run-info/${childRunId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (runInfoResponse.ok) {
          const runInfo = await runInfoResponse.json();
          if (runInfo.success) {
            // Restore workflow state
            if (runInfo.workflow_id) setChildWorkflowId(runInfo.workflow_id);
            if (runInfo.assistant_id) setAssistantId(runInfo.assistant_id);
            if (runInfo.thread_id) setThreadId(runInfo.thread_id);
            console.log('Restored workflow state:', {
              workflowId: runInfo.workflow_id,
              assistantId: runInfo.assistant_id,
              threadId: runInfo.thread_id
            });
          }
        }

        // Load saved transaction data (payment links, invoice info, etc.)
        const txResponse = await fetch(`/api/bank/v2/workflows/get-transaction/${childRunId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (txResponse.ok) {
          const txData = await txResponse.json();
          if (txData.success && txData.transaction && Object.keys(txData.transaction).length > 0) {
            console.log('🟢 [API useEffect] txData.transaction vendor:', txData.transaction.nsvendorinternalid, txData.transaction.nsvendorname);
            setTransaction(prev => {
              const merged = { ...prev, ...txData.transaction };
              console.log('🟢 [API useEffect] Merged transaction vendor:', merged.nsvendorinternalid, merged.nsvendorname);
              return merged;
            });
            console.log('Restored transaction data with payment info');

            // Set NetSuite account ID for URL generation
            if (txData.netsuite_account_id) {
              setNetsuiteAccountId(txData.netsuite_account_id);
              console.log('NetSuite account ID:', txData.netsuite_account_id);
            }

            // Restore manual allocation amounts if they exist AND no payment has been created yet
            // If payment already exists, don't restore old allocations to avoid duplicate payments
            const hasPayment = (txData.transaction.ns_payment_id?.length > 0) ||
                               (txData.transaction.ns_billpayment_id?.length > 0) ||
                               (txData.transaction.ns_je_id?.length > 0) ||
                               (txData.transaction.ns_JE_id?.length > 0);

            if (!hasPayment && txData.transaction.manual_allocations && txData.transaction.manual_allocations.length > 0) {
              const restoredAllocations = {};
              txData.transaction.manual_allocations.forEach(alloc => {
                // Support both invoice_id (new) and doc_number (legacy) field names
                const docKey = alloc.invoice_id || alloc.doc_number;
                if (docKey) {
                  restoredAllocations[docKey] = alloc.amount;
                }
              });
              setAllocationAmounts(restoredAllocations);
              setEnableReallocation(true);
              console.log('Restored manual allocations:', restoredAllocations);
            } else if (hasPayment) {
              console.log('Payment already exists, not restoring manual allocations');
            }

            // Restore TDS state
            if (txData.transaction.ns_tds_percentage) {
              setTdsPercentage(txData.transaction.ns_tds_percentage);
              setTdsNotApplicable(false);
              console.log('Restored TDS percentage:', txData.transaction.ns_tds_percentage);
            } else {
              setTdsNotApplicable(true);
            }
            if (txData.transaction.tds_not_applicable !== undefined) {
              setTdsNotApplicable(txData.transaction.tds_not_applicable);
            }

            // Restore tdsPaymentCreated state if payment exists (to hide Create Payment button)
            if (hasPayment) {
              const isVendor = txData.transaction.agent_type === 'bill_agent';
              const isJE = txData.transaction.agent_type === 'je_agent';

              let tdsPaymentData;

              if (isVendor) {
                // Vendor payment - use ns_billpayment_id / ns_billpay_doc
                const paymentIds = txData.transaction.ns_billpayment_id || [];
                const paymentDocs = txData.transaction.ns_billpay_doc || [];
                const billDocs = txData.transaction.nsbilldocnum || [];
                // Use 'vprep' for vendor prepayment, 'vendpymt' for bill payment
                // Check nsrecord_type, billlist, or if bill docs contain "Purchase Order"
                console.log('🔍 DEBUG URL CHECK:', {
                  nsrecord_type: txData.transaction.nsrecord_type,
                  billlist_nsrecord_type: txData.transaction.billlist?.[0]?.nsrecord_type,
                  billDocs: billDocs,
                  hasPurchaseOrder: billDocs.some(doc => doc && doc.includes('Purchase Order')),
                  fullTransaction: txData.transaction
                });
                const isPrepayment = txData.transaction.nsrecord_type === 'vendorPrepayment'
                  || txData.transaction.billlist?.[0]?.nsrecord_type === 'vendorPrepayment'
                  || billDocs.some(doc => doc && doc.includes('Purchase Order'));
                const vendorRecordType = isPrepayment ? 'vprep' : 'vendpymt';
                console.log('🔍 DEBUG RESULT:', { isPrepayment, vendorRecordType });

                tdsPaymentData = {
                  payment_id: paymentIds[0] || 'created',
                  payment_link: paymentIds[0] ? getNetSuiteUrl(vendorRecordType, paymentIds[0]) : null,
                  all_payments: paymentIds.map((id, idx) => ({
                    payment_id: id,
                    payment_doc: paymentDocs[idx] || `Payment ${id}`,
                    payment_url: getNetSuiteUrl(vendorRecordType, id)
                  })),
                  created_jes: []
                };
              } else if (isJE) {
                // Journal Entry - use ns_JE_id / ns_JE_doc
                const jeIds = txData.transaction.ns_JE_id || txData.transaction.ns_je_id || [];
                const jeDocs = txData.transaction.ns_JE_doc || txData.transaction.ns_je_doc || [];

                tdsPaymentData = {
                  payment_id: jeIds[0] || 'created',
                  payment_link: jeIds[0] ? getNetSuiteUrl('journal', jeIds[0]) : null,
                  all_payments: jeIds.map((id, idx) => ({
                    payment_id: id,
                    payment_doc: jeDocs[idx] || `JE ${id}`,
                    payment_url: getNetSuiteUrl('journal', id)
                  })),
                  created_jes: []
                };
              } else {
                // Customer payment - use ns_payment_id / ns_pay_doc (with TDS JEs)
                const paymentIds = txData.transaction.ns_payment_id || [];
                const paymentDocs = txData.transaction.ns_pay_doc || [];
                const jeIds = txData.transaction.JE_ID || txData.transaction.ns_je_id || [];
                const jeDocs = txData.transaction.JE_doc || txData.transaction.ns_je_doc || [];

                tdsPaymentData = {
                  payment_id: paymentIds[0] || 'created',
                  payment_link: paymentIds[0] ? getNetSuiteUrl('custpymt', paymentIds[0]) : null,
                  all_payments: paymentIds.map((id, idx) => ({
                    payment_id: id,
                    payment_doc: paymentDocs[idx] || `Payment ${id}`,
                    payment_url: getNetSuiteUrl('custpymt', id)
                  })),
                  created_jes: jeIds.map((id, idx) => ({
                    je_id: id,
                    je_doc: jeDocs[idx] || `JE ${id}`,
                    je_url: getNetSuiteUrl('journal', id)
                  }))
                };
              }

              setTdsPaymentCreated(tdsPaymentData);
              console.log('Restored tdsPaymentCreated state:', tdsPaymentData);
            }
          }
        }

        // Then load chat history
        const chatResponse = await fetch(`/api/bank/v2/workflows/chat-history/${childRunId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (chatResponse.ok) {
          const data = await chatResponse.json();
          if (data.success && data.messages && data.messages.length > 0) {
            // Helper function to parse stored message content
            const parseMessageContent = (content, type) => {
              if (!content) return '';

              // For user messages, extract user_message from JSON or text before transaction/JE data
              if (type === 'user') {
                // Handle case where content is already an object (not a string)
                if (typeof content === 'object' && content !== null) {
                  if (content.user_message) {
                    return content.user_message;
                  }
                  // Fallback: stringify and continue
                  content = JSON.stringify(content);
                }

                // First check if it's JSON with user_message field
                const trimmed = String(content).trim();
                if (trimmed.startsWith('{')) {
                  try {
                    const parsed = JSON.parse(trimmed);
                    if (parsed.user_message) {
                      return parsed.user_message;
                    }
                  } catch (e) {
                    // Not valid JSON, continue with other checks
                  }
                }

                // Check for various transaction data markers and extract text before them
                const markers = [
                  '\n\nTransaction data:',
                  '\n\nJE Lines with selected accounts:',
                  '\n\n[CRITICAL:',  // Manual allocation override marker
                  '\n\nbankdata:'
                ];

                for (const marker of markers) {
                  const markerIndex = content.indexOf(marker);
                  if (markerIndex > -1) {
                    return content.substring(0, markerIndex).trim();
                  }
                }

                return content;
              }

              // For assistant messages, try to parse JSON and extract meaningful text
              if (type === 'assistant') {
                try {
                  // Check if it starts with { - likely JSON
                  const trimmed = content.trim();
                  if (trimmed.startsWith('{')) {
                    // Strip JS-style comments (AI sometimes adds // comments in JSON)
                    const cleaned = trimmed.replace(/^\s*\/\/.*$/gm, '');
                    const parsed = JSON.parse(cleaned);

                    // First priority: user_message field (this is the AI's readable response)
                    if (parsed.user_message) {
                      return parsed.user_message;
                    }

                    // Second priority: message field
                    if (parsed.message) {
                      return parsed.message;
                    }

                    // Fallback: generate summary from bankdata
                    if (parsed.bankdata) {
                      const bankData = Array.isArray(parsed.bankdata) ? parsed.bankdata[0] : parsed.bankdata;
                      if (bankData) {
                        const amount = bankData.Amount ? `₹${Number(bankData.Amount).toLocaleString('en-IN')}` : '';
                        const customer = bankData.customername || bankData.vendorname || '';
                        return `Transaction data for ${customer}. Amount: ${amount}`;
                      }
                    }
                  }
                } catch (e) {
                  // JSON.parse failed - use regex fallback to extract user_message
                  const trimmed = content.trim();
                  const userMsgMatch = trimmed.match(/"user_message"\s*:\s*"((?:[^"\\]|\\.)*)"/s);
                  if (userMsgMatch) {
                    try {
                      return JSON.parse('"' + userMsgMatch[1] + '"');
                    } catch (e2) {
                      return userMsgMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
                    }
                  }
                }
              }

              return content;
            };

            // Filter to only user and assistant messages
            const chatMessages = data.messages
              .filter(msg => msg.type === 'user' || msg.type === 'assistant')
              .map(msg => {
                // Try to parse raw content as JSON for debug payload
                let jsonPayload = null;
                try {
                  if (msg.content && typeof msg.content === 'string') {
                    jsonPayload = JSON.parse(msg.content);
                  }
                } catch (e) {
                  // Not JSON, store raw content for debug
                  jsonPayload = msg.content;
                }
                return {
                  id: msg.id,
                  type: msg.type,
                  content: parseMessageContent(msg.content, msg.type),
                  timestamp: msg.timestamp,
                  jsonPayload: jsonPayload
                };
              })
              .filter(msg => msg.content && msg.content.trim() !== '');

            if (chatMessages.length > 0) {
              // Keep welcome message and add loaded messages
              setMessages(prev => {
                const welcomeMsg = prev.find(m => m.id === 'welcome');
                return welcomeMsg ? [welcomeMsg, ...chatMessages] : chatMessages;
              });
              console.log(`Loaded ${chatMessages.length} chat messages from history`);

              // Update transaction state with bankdata from last assistant message (for right panel)
              const lastAssistantMsg = [...chatMessages].reverse().find(m => m.type === 'assistant' && m.jsonPayload);
              console.log('🔍 [RIGHTPANEL DATA] Last assistant message jsonPayload:', lastAssistantMsg?.jsonPayload);

              if (lastAssistantMsg?.jsonPayload) {
                const payload = lastAssistantMsg.jsonPayload;
                console.log('🔍 [RIGHTPANEL DATA] payload.bankdata:', payload.bankdata);

                if (payload.bankdata) {
                  const bankdata = Array.isArray(payload.bankdata) ? payload.bankdata[0] : payload.bankdata;
                  console.log('🔍 [RIGHTPANEL DATA] Extracted bankdata:', bankdata);
                  console.log('🔍 [RIGHTPANEL DATA] bankdata.billlist:', bankdata?.billlist);

                  if (bankdata && (bankdata.billlist?.length > 0 || bankdata.invoicelist?.length > 0)) {
                    // Update transaction with bill/invoice data for right panel
                    setTransaction(prev => ({
                      ...prev,
                      billlist: bankdata.billlist || prev.billlist || [],
                      nsbillinternalid: bankdata.nsbillinternalid || prev.nsbillinternalid || [],
                      nsbilldocnum: bankdata.nsbilldocnum || prev.nsbilldocnum || [],
                      nsbillamount: bankdata.nsbillamount || prev.nsbillamount || [],
                      nsrequest_id: bankdata.nsrequest_id || prev.nsrequest_id || '',
                      nsrecord_type: bankdata.nsrecord_type || prev.nsrecord_type || '',
                      invoicelist: bankdata.invoicelist || prev.invoicelist || [],
                      nsinvoiceinternalid: bankdata.nsinvoiceinternalid || prev.nsinvoiceinternalid || [],
                      nsinvoicedocnum: bankdata.nsinvoicedocnum || prev.nsinvoicedocnum || [],
                      nsinvoiceamount: bankdata.nsinvoiceamount || prev.nsinvoiceamount || []
                    }));
                    console.log('🔍 [RIGHTPANEL DATA] Updated transaction state with bankdata from chat history');
                  }
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Error loading existing session:', err);
      }
    };

    loadExistingSession();
  }, [childRunId, token]);

  // Check for existing workflow on page load (when coming from TransactionsModal without childRun in URL)
  useEffect(() => {
    const checkExistingWorkflow = async () => {
      // Only run if we have parentRunId but no childRunId
      if (!parentRunId || childRunId || !token || !transaction) return;

      console.log('Checking for existing workflow on page load...');

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
        const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/create-child`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            parent_workflow_id: parentWorkflowId,
            parent_run_id: parentRunId,
            transaction_data: transaction,
            workflow_type: 'transaction_approval'
          })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Existing workflow check result:', data);

          if (data.child_run_id) {
            // Found or created workflow - set states
            setChildWorkflowId(data.workflow_id);
            setChildRunId(data.child_run_id);
            if (data.assistant_id) setAssistantId(data.assistant_id);
            if (data.thread_id) {
              setThreadId(data.thread_id);
            }
            // Set NetSuite account ID for URL generation
            if (data.netsuite_account_id) {
              setNetsuiteAccountId(data.netsuite_account_id);
              console.log('NetSuite account ID from create-child:', data.netsuite_account_id);
            }

            // For existing workflows, update transaction with latest data (may have manual entity mappings)
            if (data.existing && data.updated_transaction) {
              console.log('🔄 Updating transaction with latest data from existing workflow:', data.updated_transaction);
              const mergedTransaction = {
                ...transaction,
                ...data.updated_transaction,
                transaction_index: transaction.transaction_index // Preserve original index
              };
              setTransaction(mergedTransaction);
            }

            // For NEW workflows - trigger AI to fetch invoices
            // DON'T use pre-fetched data for customer payments (bank_agent) - wait for AI response
            if (!data.existing) {
              // Trigger AI to show allocation message only if entity is mapped
              // Skip for JE agent - it doesn't use invoice/bill allocation
              // Skip for unmapped vendor/customer transactions - user needs to select entity first
              const isMapped = transaction.agent_type === 'bill_agent'
                ? !!transaction.nsvendorinternalid
                : transaction.agent_type === 'bank_agent'
                ? !!transaction.nscustomerinternalid
                : true; // JE agent doesn't need mapping check

              if (transaction && transaction.agent_type !== 'je_agent' && isMapped) {
                // Wait for AI response to populate invoicelist/billlist and allocations
                // This ensures the right panel only updates AFTER AI responds
                let updatedTx;
                if (transaction.agent_type === 'bill_agent') {
                  // Vendor payment - clear bill fields
                  updatedTx = {
                    ...transaction,
                    billlist: [],  // Will be populated by AI response
                    nsbilldocnum: [],
                    nsbillamount: [],
                    nsbillinternalid: []
                  };
                  console.log('📊 [BILL_AGENT] Waiting for AI to fetch bills - NOT using pre-fetch');
                } else {
                  // Customer payment - clear invoice fields
                  updatedTx = {
                    ...transaction,
                    invoicelist: [],  // Will be populated by AI response
                    nsinvoicedocnum: [],
                    nsinvoiceamount: [],
                    nsinvoiceinternalid: [],
                    creditsarray: []
                  };
                  console.log('📊 [BANK_AGENT] Waiting for AI to fetch invoices - NOT using pre-fetch');
                }
                setTransaction(updatedTx);

                // Immediately call AI with pre-fetched data
                (async () => {
                  try {
                    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
                    const currentAssistantId = data.assistant_id;
                    const currentChildRunId = data.child_run_id;

                    // Create thread
                    let currentThreadId = data.thread_id;
                    if (!currentThreadId) {
                      console.log('🧵 Creating thread for auto-trigger...');
                      const threadResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/thread/create`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          assistant_id: currentAssistantId,
                          run_id: currentChildRunId
                        })
                      });

                      if (threadResponse.ok) {
                        const threadData = await threadResponse.json();
                        currentThreadId = threadData.thread_id;
                        setThreadId(currentThreadId);
                      } else {
                        console.error('Failed to create thread for auto-trigger');
                        return;
                      }
                    }

                    // Prepare message based on agent type
                    // bank_agent = Customer Payment (AR), bill_agent = Vendor Payment (AP), je_agent = Journal Entry
                    const agentType = updatedTx.agent_type;
                    let entityName, dataType, autoMessage;

                    if (agentType === 'je_agent') {
                      entityName = 'Journal Entry';
                      dataType = 'accounts';
                      autoMessage = `Process this transaction as a Journal Entry`;
                    } else if (agentType === 'bill_agent') {
                      entityName = updatedTx.nsvendorname || updatedTx.vendorname || 'Vendor';
                      dataType = 'bills';
                      autoMessage = `Show me the open ${dataType} for ${entityName}`;
                    } else {
                      // bank_agent or default = Customer Payment
                      entityName = updatedTx.nscustomername || updatedTx.customername || 'Customer';
                      dataType = 'invoices';
                      autoMessage = `Show me the open ${dataType} for ${entityName}`;
                    }
                    console.log('🔍 [AUTO-TRIGGER] agent_type:', agentType, 'transactiontype:', updatedTx.transactiontype);

                    // Prepare transaction schema (JSON sent to AI) - matches prompt schema exactly
                    let txSchema;
                    if (agentType === 'je_agent') {
                      // Journal Entry schema
                      console.log('🔍 [AUTO-TRIGGER] Using JE schema');
                      txSchema = {
                        Slno: updatedTx.Slno || updatedTx.transaction_index || '',
                        trandate: updatedTx.trandate || '',
                        transactiontype: updatedTx.transactiontype || '',
                        Amount: updatedTx.Amount || '',
                        description: updatedTx.description || '',
                        bank: updatedTx.bank || '',
                        nsaccountid: updatedTx.nsaccountid || null,
                        nsaccountname: updatedTx.nsaccountname || '',
                        include: updatedTx.include !== false,
                        ns_JE_id: updatedTx.ns_JE_id || [],
                        ns_JE_doc: updatedTx.ns_JE_doc || [],
                        viewje: updatedTx.viewje || ''
                      };
                    } else if (agentType === 'bill_agent') {
                      // Vendor Payment (AP) schema - NO TDS fields
                      console.log('🔍 [AUTO-TRIGGER] Using VENDOR schema');
                      txSchema = {
                        Amount: updatedTx.Amount || '',
                        bank: updatedTx.bank || '',
                        vendorname: updatedTx.vendorname || '',
                        description: updatedTx.description || '',
                        billlist: updatedTx.billlist || [],
                        billnumber: updatedTx.billnumber || '',
                        ns_billpay_doc: updatedTx.ns_billpay_doc || [],
                        ns_billpayment_id: updatedTx.ns_billpayment_id || [],
                        nsvendorinternalid: updatedTx.nsvendorinternalid || '',
                        nsvendorname: updatedTx.nsvendorname || '',
                        nsbillamount: updatedTx.nsbillamount || [],
                        nsbilldocnum: updatedTx.nsbilldocnum || [],
                        nsbillinternalid: updatedTx.nsbillinternalid || [],
                        nsrequest_id: updatedTx.nsrequest_id || '',
                        nsrecord_type: updatedTx.nsrecord_type || '',
                        trandate: updatedTx.trandate || '',
                        transactiontype: updatedTx.transactiontype || 'debit'
                      };
                    } else {
                      // Customer Payment (AR) schema - WITH TDS support (bank_agent or default)
                      console.log('🔍 [AUTO-TRIGGER] Using CUSTOMER schema');
                      txSchema = {
                        Amount: updatedTx.Amount || '',
                        bank: updatedTx.bank || '',
                        customername: updatedTx.customername || '',
                        description: updatedTx.description || '',
                        invoicelist: updatedTx.invoicelist || [],
                        invoicenumber: updatedTx.invoicenumber || '',
                        ns_pay_doc: updatedTx.ns_pay_doc || [],
                        ns_payment_id: updatedTx.ns_payment_id || [],
                        JE_doc: updatedTx.JE_doc || [],
                        JE_ID: updatedTx.JE_ID || [],
                        nscustomerinternalid: updatedTx.nscustomerinternalid || '',
                        nscustomername: updatedTx.nscustomername || '',
                        ns_tds_percentage: updatedTx.ns_tds_percentage || '',
                        nsinvoiceamount: [],
                        nsinvoicedocnum: [],
                        nsinvoiceinternalid: [],
                        creditsarray: [],
                        trandate: updatedTx.trandate || '',
                        transactiontype: updatedTx.transactiontype || 'credit'
                      };
                    }

                    // Build the exact JSON structure for AI
                    const messageForAI = {
                      user_message: autoMessage,
                      bankdata: txSchema,
                      message_type: 'user_message'
                    };

                    // Show user message with JSON payload for debug
                    const userMsgId = `auto-${Date.now()}`;
                    setMessages([{
                      id: userMsgId,
                      type: 'user',
                      content: autoMessage,
                      timestamp: new Date().toISOString(),
                      jsonPayload: messageForAI
                    }]);
                    setIsLoading(true);
                    setTransactionDataSent(true);

                    // Send JSON string to AI
                    const formattedMessage = JSON.stringify(messageForAI, null, 2);

                    // Send to AI - include child_run_id in transaction_context for message persistence
                    const sendResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/message/send`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        thread_id: currentThreadId,
                        assistant_id: currentAssistantId,
                        message: formattedMessage,
                        transaction_context: {
                          ...updatedTx,
                          child_run_id: currentChildRunId
                        }
                      })
                    });

                    if (sendResponse.ok) {
                      const respData = await sendResponse.json();
                      let aiContent = respData.response || respData.message;
                      let rawAiResponse = aiContent; // Store raw response for debug

                      // Extract user_message from JSON response if present
                      let parsedAiJson = null;
                      if (aiContent) {
                        try {
                          parsedAiJson = typeof aiContent === 'string' ? JSON.parse(aiContent) : aiContent;
                          if (parsedAiJson.user_message) {
                            aiContent = parsedAiJson.user_message;
                          }
                        } catch (e) {
                          // Not JSON, use as-is
                        }
                      }

                      if (aiContent) {
                        setMessages(prev => [...prev, {
                          id: `ai-${Date.now()}`,
                          type: 'assistant',
                          content: aiContent,
                          timestamp: new Date().toISOString(),
                          jsonPayload: parsedAiJson || rawAiResponse
                        }]);
                      }

                      // Update transaction with bankdata from AI response (invoicelist/billlist, allocations, etc.)
                      // Backend returns: response (text), response_json (full JSON), bankdata (extracted array)
                      // Use respData.bankdata or respData.response_json.bankdata - NOT parsedAiJson from text
                      let aiBankdata = null;
                      const bankdataSource = respData.bankdata || respData.response_json?.bankdata;
                      console.log('📊 [AUTO-TRIGGER] respData.bankdata:', respData.bankdata);
                      console.log('📊 [AUTO-TRIGGER] respData.response_json?.bankdata:', respData.response_json?.bankdata);

                      if (bankdataSource) {
                        aiBankdata = Array.isArray(bankdataSource) ? bankdataSource[0] : bankdataSource;
                      } else if (parsedAiJson) {
                        // Fallback to parsed text (legacy)
                        if (parsedAiJson.bankdata) {
                          aiBankdata = Array.isArray(parsedAiJson.bankdata)
                            ? parsedAiJson.bankdata[0]
                            : parsedAiJson.bankdata;
                        } else if (parsedAiJson.billlist || parsedAiJson.invoicelist) {
                          aiBankdata = parsedAiJson;
                        }
                      }
                      console.log('📊 [AUTO-TRIGGER] aiBankdata:', aiBankdata);
                      console.log('📊 [AUTO-TRIGGER] aiBankdata.billlist:', aiBankdata?.billlist);

                      if (aiBankdata) {
                        // Use updatedTx.agent_type directly since it's in scope and reliable
                        const agentType = updatedTx.agent_type;
                        console.log('📊 [AUTO-TRIGGER AI RESPONSE] Using agent_type:', agentType);

                        // Build updated transaction based on agent type
                        let updatedFields = {};
                        if (agentType === 'bill_agent') {
                          // Vendor payment - update bill fields
                          const billlist = aiBankdata.billlist || [];
                          console.log('📊 [AUTO-TRIGGER] Updating VENDOR fields, billlist:', billlist.length, billlist);
                          updatedFields = {
                            billlist: billlist,
                            nsbilldocnum: aiBankdata.nsbilldocnum || [],
                            nsbillamount: aiBankdata.nsbillamount || [],
                            nsbillinternalid: aiBankdata.nsbillinternalid || [],
                            ns_billpayment_id: aiBankdata.ns_billpayment_id || [],
                            ns_billpay_doc: aiBankdata.ns_billpay_doc || []
                          };
                        } else if (agentType === 'je_agent') {
                          // Journal Entry - update JE fields
                          console.log('📊 [AUTO-TRIGGER] Updating JE fields');
                          updatedFields = {
                            nsaccountid: aiBankdata.nsaccountid || null,
                            nsaccountname: aiBankdata.nsaccountname || '',
                            ns_JE_id: aiBankdata.ns_JE_id || [],
                            ns_JE_doc: aiBankdata.ns_JE_doc || [],
                            viewje: aiBankdata.viewje || ''
                          };
                        } else {
                          // Customer payment - update invoice fields
                          console.log('📊 [AUTO-TRIGGER] Updating CUSTOMER fields, invoicelist:', aiBankdata.invoicelist?.length);
                          updatedFields = {
                            invoicelist: aiBankdata.invoicelist || [],
                            nsinvoicedocnum: aiBankdata.nsinvoicedocnum || [],
                            nsinvoiceamount: aiBankdata.nsinvoiceamount || [],
                            nsinvoiceinternalid: aiBankdata.nsinvoiceinternalid || [],
                            creditsarray: aiBankdata.creditsarray || [],
                            ns_tds_percentage: aiBankdata.ns_tds_percentage || ''
                          };
                        }

                        // Update state
                        setTransaction(prev => ({ ...prev, ...updatedFields }));

                        // For vendor payments, explicitly calculate allocation after setting billlist
                        // This ensures allocation panel updates immediately without waiting for useEffect
                        if (agentType === 'bill_agent' && updatedFields.billlist?.length > 0) {
                          const paymentAmount = Math.abs(parseFloat(updatedTx.Amount) || 0);
                          if (paymentAmount > 0) {
                            console.log('📊 [AUTO-TRIGGER] Calculating vendor allocation immediately');
                            const billlist = updatedFields.billlist;
                            let remainingPayment = paymentAmount;
                            const allocations = [];

                            // Sort by due date (oldest first)
                            const parseDate = (dateStr) => {
                              if (!dateStr) return new Date('9999-12-31');
                              const parts = dateStr.split('/');
                              if (parts.length === 3) {
                                return new Date(parts[2], parts[0] - 1, parts[1]);
                              }
                              return new Date(dateStr);
                            };
                            const sortedBills = [...billlist].sort((a, b) => parseDate(a.duedate) - parseDate(b.duedate));

                            for (const bill of sortedBills) {
                              if (remainingPayment <= 0) break;
                              const billDue = Math.abs(parseFloat(bill.bill_total) || 0);
                              if (billDue <= 0) continue;

                              const paymentForBill = Math.min(remainingPayment, billDue);
                              const billNum = bill.billnumber || bill.BillNumber;
                              const billInternalId = bill.internalid;

                              allocations.push({
                                invoice_number: billNum,
                                invoice_id: billInternalId || billNum,
                                invoice_due: billDue,
                                original_total: billDue,
                                totpayamount: billDue,
                                taxtotal: 0,
                                expected_payment: Math.round(billDue * 100) / 100,
                                payment_allocated: Math.round(paymentForBill * 100) / 100,
                                tds_amount: 0,
                                amount_applied: Math.round(paymentForBill * 100) / 100,
                                is_partial: paymentForBill < billDue,
                                is_manual: false
                              });
                              remainingPayment -= paymentForBill;
                            }

                            setTdsAllocation({
                              allocations,
                              total_tds: 0,
                              total_payment: paymentAmount,
                              tds_percentage: 0,
                              is_manual: false,
                              tds_not_applicable: true
                            });
                            console.log('📊 [AUTO-TRIGGER] Vendor allocation set:', allocations.length, 'bills');
                          }
                        }

                        // Save to database for persistence on page refresh
                        const txToSave = { ...updatedTx, ...updatedFields };
                        console.log('💾 [AUTO-TRIGGER] Saving transaction to DB:', currentChildRunId);
                        fetch(`${API_BASE_URL}/api/bank/v2/workflows/save-transaction/${currentChildRunId}`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ transaction: txToSave })
                        }).catch(err => console.warn('Failed to save transaction:', err));
                      }
                    }
                  } catch (err) {
                    console.error('Auto-trigger AI error:', err);
                  } finally {
                    setIsLoading(false);
                    setLoadingAction(null);
                  }
                })();
              }
            }

            // Update URL with childRunId for persistence
            const newParams = new URLSearchParams(searchParams);
            newParams.set('childRun', data.child_run_id);
            navigate(`${window.location.pathname}?${newParams.toString()}`, { replace: true });

            console.log(data.existing ? 'Restored existing workflow:' : 'Created new workflow:', data.child_run_id);
          }
        }
      } catch (err) {
        console.error('Error checking for existing workflow:', err);
      }
    };

    checkExistingWorkflow();
  }, [parentRunId, parentWorkflowId, transaction, token, childRunId]);

  // Fetch all entities when NOT matched (for dropdown selection)
  useEffect(() => {
    const fetchAllEntities = async () => {
      if (!transaction || !parentRunId || !token) return;

      // Determine if matched based on agent type
      // bank_agent (AR) = customers, bill_agent (AP) = vendors
      const isBillAgent = transaction.agent_type === 'bill_agent';
      const matched = isBillAgent
        ? !!transaction.nsvendorinternalid
        : !!transaction.nscustomerinternalid;

      // Don't fetch if already matched
      if (matched) {
        setAllEntities([]);
        return;
      }

      setIsLoadingEntities(true);

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

        // IMPORTANT: Fetch correct entity type based on agent
        // bill_agent (AP) = vendors, bank_agent (AR) = customers
        const entityEndpoint = isBillAgent ? 'vendors' : 'customers';

        console.log(`🔍 Fetching ALL ${entityEndpoint} for ${transaction.agent_type}`);

        const entityResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/${entityEndpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (entityResponse.ok) {
          const entityData = await entityResponse.json();
          const entities = (entityData.data || []).map(e => ({
            value: e.value,
            label: e.label
          }));
          setAllEntities(entities);
          console.log(`✅ Fetched ${entities.length} ${entityEndpoint} for selection`);
        } else {
          console.error(`Failed to fetch ${entityEndpoint}:`, entityResponse.status);
        }
      } catch (err) {
        console.error('Error fetching entities:', err);
      } finally {
        setIsLoadingEntities(false);
      }
    };

    fetchAllEntities();
  }, [transaction, parentRunId, token]);

  // Fetch accounts, classes, and locations for JE Agent
  useEffect(() => {
    const fetchAccountsForJE = async () => {
      if (!transaction || transaction.agent_type !== 'je_agent' || !token) return;

      setIsLoadingAccounts(true);

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
        const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/accounts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAccountGroups(data.groups || {});
          console.log(`📊 Fetched ${data.count || 0} accounts in ${Object.keys(data.groups || {}).length} groups`);
        } else {
          console.error('Failed to fetch accounts:', response.status);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    // Fetch departments, classes and locations for JE Agent
    const fetchDepartmentsClassesLocations = async () => {
      if (!transaction || transaction.agent_type !== 'je_agent' || !token) return;

      setIsLoadingClassesLocations(true);

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

        // Fetch departments, classes and locations in parallel
        const [departmentsRes, classesRes, locationsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/departments`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/classes`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/locations`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);

        if (departmentsRes.ok) {
          const departmentsData = await departmentsRes.json();
          setDepartmentList(departmentsData.items || []);
          console.log(`📊 Fetched ${departmentsData.count || 0} departments`);
        } else {
          console.error('Failed to fetch departments:', departmentsRes.status);
        }

        if (classesRes.ok) {
          const classesData = await classesRes.json();
          setClassList(classesData.items || []);
          console.log(`📊 Fetched ${classesData.count || 0} classes`);
        } else {
          console.error('Failed to fetch classes:', classesRes.status);
        }

        if (locationsRes.ok) {
          const locationsData = await locationsRes.json();
          setLocationList(locationsData.items || []);
          console.log(`📊 Fetched ${locationsData.count || 0} locations`);
        } else {
          console.error('Failed to fetch locations:', locationsRes.status);
        }
      } catch (err) {
        console.error('Error fetching departments/classes/locations:', err);
      } finally {
        setIsLoadingClassesLocations(false);
      }
    };

    // Fetch customers and vendors for JE Agent
    const fetchCustomersVendors = async () => {
      if (!transaction || transaction.agent_type !== 'je_agent' || !token) return;

      setIsLoadingCustomersVendors(true);

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

        // Fetch customers and vendors in parallel
        const [customersRes, vendorsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/customers`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/vendors`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);

        if (customersRes.ok) {
          const customersData = await customersRes.json();
          setCustomerList(customersData.data || []);
          console.log(`📊 Fetched ${customersData.count || 0} customers for JE`);
        } else {
          console.error('Failed to fetch customers:', customersRes.status);
        }

        if (vendorsRes.ok) {
          const vendorsData = await vendorsRes.json();
          setVendorList(vendorsData.data || []);
          console.log(`📊 Fetched ${vendorsData.count || 0} vendors for JE`);
        } else {
          console.error('Failed to fetch vendors:', vendorsRes.status);
        }
      } catch (err) {
        console.error('Error fetching customers/vendors:', err);
      } finally {
        setIsLoadingCustomersVendors(false);
      }
    };

    // Only fetch JE data once, and skip if JE is already created
    const jeAlreadyCreated = transaction?.ns_JE_id?.length > 0 || transaction?.ns_je_id?.length > 0;
    if (transaction?.agent_type === 'je_agent' && !jeDataFetchedRef.current && !jeAlreadyCreated) {
      jeDataFetchedRef.current = true;
      fetchAccountsForJE();
      fetchDepartmentsClassesLocations();
      fetchCustomersVendors();
    }

    // Initialize JE lines from transaction data - but only if jeLines is empty
    // This prevents resetting user's selections when transaction updates from AI response
    if (transaction && transaction.agent_type === 'je_agent') {
      setJeLines(prevJeLines => {
        // If we already have jeLines with data, don't reset them
        if (prevJeLines.length > 0) {
          console.log(`📊 JE Lines already exist (${prevJeLines.length} lines), preserving user selections`);
          return prevJeLines;
        }

        // Check if we have explicit je_lines or lines array
        let lines = transaction.je_lines || transaction.lines || [];

        // If no lines array, create a single line from the transaction itself
        if (lines.length === 0) {
          lines = [{
            srno: 1,
            description: transaction.description || '',
            memo: transaction.description || '',
            transactiontype: transaction.transactiontype || (parseFloat(transaction.Amount) >= 0 ? 'credit' : 'debit'),
            type: parseFloat(transaction.Amount) >= 0 ? 'credit' : 'debit',
            Amount: transaction.Amount,
            amount: transaction.Amount,
            debit: parseFloat(transaction.Amount) < 0 ? Math.abs(transaction.Amount) : 0,
            credit: parseFloat(transaction.Amount) >= 0 ? transaction.Amount : 0,
            nsaccountid: transaction.nsaccountid || '',
            nsaccountname: transaction.nsaccountname || '',
            account_name: transaction.nsaccountname || '',
            current_account: transaction.nsaccountname || '',
            trandate: transaction.trandate,
            bank: transaction.bank,
            // Original transaction reference
            customername: transaction.customername,
            vendorname: transaction.vendorname,
            original_agent_type: transaction.original_agent_type
          }];
        }

        console.log(`📊 JE Lines initialized: ${lines.length} line(s)`);

        // Initialize selection states from line data (to preserve values after refresh)
        const initialAccounts = {};
        const initialMemos = {};
        const initialDepartments = {};
        const initialClasses = {};
        const initialLocations = {};
        const initialCustomers = {};
        const initialVendors = {};

        lines.forEach((line, idx) => {
          if (line.nsaccountid) initialAccounts[idx] = String(line.nsaccountid);
          if (line.memo) initialMemos[idx] = line.memo;
          if (line.department_id) initialDepartments[idx] = String(line.department_id);
          if (line.class_id) initialClasses[idx] = String(line.class_id);
          if (line.location_id) initialLocations[idx] = String(line.location_id);
          if (line.customer_id) initialCustomers[idx] = String(line.customer_id);
          if (line.vendor_id) initialVendors[idx] = String(line.vendor_id);
        });

        // Set initial values if any exist
        if (Object.keys(initialAccounts).length > 0) setJeLineUpdates(initialAccounts);
        if (Object.keys(initialMemos).length > 0) setJeLineMemos(initialMemos);
        if (Object.keys(initialDepartments).length > 0) setJeLineDepartments(initialDepartments);
        if (Object.keys(initialClasses).length > 0) setJeLineClasses(initialClasses);
        if (Object.keys(initialLocations).length > 0) setJeLineLocations(initialLocations);
        if (Object.keys(initialCustomers).length > 0) setJeLineCustomers(initialCustomers);
        if (Object.keys(initialVendors).length > 0) setJeLineVendors(initialVendors);

        console.log(`📊 JE Line selections initialized from saved data`);

        return lines;
      });
    }
  }, [transaction, token]);

  const getTransactionTypeName = (agentType) => {
    switch (agentType) {
      case 'bank_agent': return 'Customer Payment (AR)';
      case 'bill_agent': return 'Vendor Payment (AP)';
      case 'je_agent': return 'Journal Entry';
      default: return agentType;
    }
  };

  const formatAmount = (n) => {
    const num = Number(n || 0);
    return num.toLocaleString('en-IN');
  };

  // Check if entity is matched
  const isMatched = () => {
    if (!transaction) return false;
    if (transaction.agent_type === 'bill_agent') {
      return !!transaction.nsvendorinternalid;
    }
    return !!transaction.nscustomerinternalid;
  };

  const getMatchedName = () => {
    if (!transaction) return '';
    if (transaction.agent_type === 'bill_agent') {
      return transaction.nsvendorname || '';
    }
    return transaction.nscustomername || '';
  };

  const getPredictedName = () => {
    if (!transaction) return 'N/A';
    if (transaction.agent_type === 'bill_agent') {
      return transaction.vendorname || 'N/A';
    }
    return transaction.customername || 'N/A';
  };

  // Get similar entities for dropdown
  const getSimilarEntities = () => {
    if (!transaction) return [];
    if (transaction.agent_type === 'bank_agent') {
      return (transaction.similarcustomer || []).map(entity => ({
        value: typeof entity === 'object' ? entity.internalid : entity,
        label: typeof entity === 'object' ? entity.name : entity
      }));
    } else if (transaction.agent_type === 'bill_agent') {
      return (transaction.similarvendor || []).map(entity => ({
        value: typeof entity === 'object' ? entity.internalid : entity,
        label: typeof entity === 'object' ? entity.name : entity
      }));
    }
    return [];
  };

  // Get open invoices/bills
  const getOpenDocs = () => {
    console.log('🧾 [getOpenDocs] Called, agent_type:', transaction?.agent_type, 'billlist length:', transaction?.billlist?.length, 'invoicelist length:', transaction?.invoicelist?.length);
    if (!transaction) return [];
    if (transaction.agent_type === 'bank_agent') {
      return (transaction.invoicelist || []).map((inv, idx) => ({
        id: inv.internalid || transaction.nsinvoiceinternalid?.[idx] || `inv-${idx}`,
        number: inv.invoicenumber || inv.InvoiceNumber || inv.docnum || `Invoice ${idx + 1}`,
        amount: inv.invoice_total || inv.openbalance || inv.amount || 0,
        duedate: inv.duedate || '',
        // TDS related fields
        totpayamount: inv.totpayamount || 0,
        taxtotal: inv.taxtotal || 0,
        originalTotal: (parseFloat(inv.totpayamount) || 0) + (parseFloat(inv.taxtotal) || 0)
      }));
    } else if (transaction.agent_type === 'bill_agent') {
      return (transaction.billlist || []).map((bill, idx) => ({
        id: bill.internalid || transaction.nsbillinternalid?.[idx] || `bill-${idx}`,
        number: bill.billnumber || bill.BillNumber || bill.docnum || `Bill ${idx + 1}`,
        amount: bill.bill_total || bill.openbalance || bill.amount || 0,
        duedate: bill.duedate || ''
      }));
    }
    return [];
  };

  // Get suggested allocation (prefers manual_allocations if available)
  const getSuggestedAllocation = () => {
    if (!transaction) return [];

    // If user has set manual allocations, use those instead
    if (transaction.manual_allocations && transaction.manual_allocations.length > 0) {
      return transaction.manual_allocations.map(alloc => ({
        // Support both invoice_id (new) and doc_number (legacy) field names
        number: alloc.invoice_id || alloc.doc_number || 'Unknown',
        amount: alloc.amount,
        isManual: true
      }));
    }

    if (transaction.agent_type === 'bank_agent') {
      // First try nsinvoicedocnum/nsinvoiceamount (AI suggested allocation)
      const docNums = transaction.nsinvoicedocnum || [];
      const amounts = transaction.nsinvoiceamount || [];
      if (docNums.length > 0) {
        return docNums.map((num, idx) => ({
          number: num,
          amount: amounts[idx] || 0
        }));
      }
      // Fallback to invoicelist if no AI allocation
      const invoiceList = transaction.invoicelist || [];
      if (invoiceList.length > 0) {
        return invoiceList.map((inv) => ({
          number: inv.invoicenumber || inv.docnum || 'Invoice',
          amount: inv.invoice_total || inv.openbalance || inv.amount || 0
        }));
      }
      return [];
    } else if (transaction.agent_type === 'bill_agent') {
      // First try nsbilldocnum/nsbillamount (AI suggested allocation)
      const docNums = transaction.nsbilldocnum || [];
      const amounts = transaction.nsbillamount || [];
      if (docNums.length > 0) {
        return docNums.map((num, idx) => ({
          number: num,
          amount: amounts[idx] || 0
        }));
      }
      // Fallback to billlist if no AI allocation
      const billList = transaction.billlist || [];
      if (billList.length > 0) {
        return billList.map((bill) => ({
          number: bill.billnumber || bill.BillNumber || bill.docnum || 'Bill',
          amount: bill.bill_total || bill.openbalance || bill.amount || 0
        }));
      }
      return [];
    }
    return [];
  };

  // Get payment links
  const getPaymentLinks = () => {
    if (!transaction) return [];
    console.log('🔍 [PAYMENT LINKS] Checking transaction:', {
      agent_type: transaction.agent_type,
      ns_billpayment_id: transaction.ns_billpayment_id,
      ns_billpay_doc: transaction.ns_billpay_doc,
      ns_payment_id: transaction.ns_payment_id,
      ns_pay_doc: transaction.ns_pay_doc
    });
    if (transaction.agent_type === 'bank_agent') {
      const ids = transaction.ns_payment_id || [];
      const docs = transaction.ns_pay_doc || [];
      return ids.map((id, idx) => ({
        id,
        label: docs[idx] || `Payment ${id}`,
        url: getNetSuiteUrl('custpymt', id),
        type: 'customer'
      }));
    } else if (transaction.agent_type === 'bill_agent') {
      const ids = transaction.ns_billpayment_id || [];
      const docs = transaction.ns_billpay_doc || [];
      const billDocs = transaction.nsbilldocnum || [];
      // Use 'vprep' for vendor prepayment, 'vendpymt' for bill payment
      const isPrepayment = transaction.nsrecord_type === 'vendorPrepayment'
        || transaction.billlist?.[0]?.nsrecord_type === 'vendorPrepayment'
        || billDocs.some(doc => doc && doc.includes('Purchase Order'));
      const vendorRecordType = isPrepayment ? 'vprep' : 'vendpymt';
      console.log('🔍 [PAYMENT LINKS] Vendor payment - ids:', ids, 'docs:', docs, 'recordType:', vendorRecordType, 'isPrepayment:', isPrepayment);
      return ids.map((id, idx) => ({
        id,
        label: docs[idx] || `Payment ${id}`,
        url: getNetSuiteUrl(vendorRecordType, id),
        type: 'vendor'
      }));
    } else if (transaction.agent_type === 'je_agent') {
      // Handle both ns_je_id and ns_JE_id (backend uses capital JE)
      // Use length check since empty array is truthy
      const ids = (transaction.ns_JE_id?.length > 0 ? transaction.ns_JE_id : null) || (transaction.ns_je_id?.length > 0 ? transaction.ns_je_id : null) || [];
      const docs = (transaction.ns_JE_doc?.length > 0 ? transaction.ns_JE_doc : null) || (transaction.ns_je_doc?.length > 0 ? transaction.ns_je_doc : null) || [];
      console.log('🔍 JE Payment Links Debug:', {
        agent_type: transaction.agent_type,
        ns_JE_id: transaction.ns_JE_id,
        ns_je_id: transaction.ns_je_id,
        ns_JE_doc: transaction.ns_JE_doc,
        ns_je_doc: transaction.ns_je_doc,
        ids,
        docs
      });
      return ids.map((id, idx) => ({
        id,
        label: docs[idx] || `JE ${id}`,
        url: getNetSuiteUrl('journal', id),
        type: 'je'
      }));
    }
    return [];
  };

  // Create child workflow
  const createChildWorkflow = async () => {
    if (!parentWorkflowId || !parentRunId) {
      console.error('Missing parent workflow information');
      return null;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/create-child`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_workflow_id: parentWorkflowId,
          parent_run_id: parentRunId,
          transaction_data: transaction,
          workflow_type: 'transaction_approval'
        })
      });

      if (!response.ok) throw new Error('Failed to create child workflow');

      const data = await response.json();
      setChildWorkflowId(data.workflow_id);
      setChildRunId(data.child_run_id);
      setAssistantId(data.assistant_id);

      // If existing workflow returned thread_id, use it
      if (data.thread_id) {
        setThreadId(data.thread_id);
        console.log('Using existing thread:', data.thread_id);
      }

      // Update URL with childRunId for persistence on page reload
      const newParams = new URLSearchParams(searchParams);
      newParams.set('childRun', data.child_run_id);
      navigate(`${window.location.pathname}?${newParams.toString()}`, { replace: true });

      return { workflowId: data.workflow_id, runId: data.child_run_id, assistantId: data.assistant_id, threadId: data.thread_id };
    } catch (err) {
      console.error('Error creating child workflow:', err);
      setError('Failed to create child workflow');
      return null;
    }
  };

  // Create OpenAI thread
  const createThread = async (currentAssistantId = null, currentChildRunId = null) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const effectiveAssistantId = currentAssistantId || assistantId;
      const effectiveChildRunId = currentChildRunId || childRunId;

      if (!effectiveAssistantId) throw new Error('No assistant ID available');

      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/thread/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          assistant_id: effectiveAssistantId,
          metadata: {
            transaction_type: transaction.agent_type,
            transaction_index: transaction.transaction_index,
            parent_workflow_id: parentWorkflowId,
            parent_run_id: parentRunId,
            child_workflow_id: childWorkflowId,
            child_run_id: effectiveChildRunId
          }
        })
      });

      if (!response.ok) throw new Error('Failed to create thread');

      const data = await response.json();
      setThreadId(data.thread_id);
      return data.thread_id;
    } catch (err) {
      console.error('Error creating thread:', err);
      setError('Failed to initialize chat');
      return null;
    }
  };

  // Send message to OpenAI
  const sendMessageToAssistant = async (message, currentThreadId, currentChildRunId = null, currentAssistantId = null) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const effectiveAssistantId = currentAssistantId || assistantId;
      const effectiveChildRunId = currentChildRunId || childRunId;

      if (!effectiveAssistantId) throw new Error('No assistant ID available');

      // Check if allocation is available (works for both TDS and non-TDS)
      const hasAllocation = tdsAllocation &&
                            tdsAllocation.allocations &&
                            tdsAllocation.allocations.length > 0;

      // Build arrays from allocation if available
      let effectiveNsinvoiceinternalid = transaction.nsinvoiceinternalid || [];
      let effectiveNsinvoicedocnum = transaction.nsinvoicedocnum || [];
      let effectiveNsinvoiceamount = transaction.nsinvoiceamount || [];
      let effectiveCreditsarray = transaction.creditsarray || [];

      if (hasAllocation) {
        effectiveNsinvoiceinternalid = [];
        effectiveNsinvoicedocnum = [];
        effectiveNsinvoiceamount = [];
        effectiveCreditsarray = [];
        for (const alloc of tdsAllocation.allocations) {
          effectiveNsinvoiceinternalid.push(String(alloc.invoice_id));
          effectiveNsinvoicedocnum.push(alloc.invoice_number);
          // For non-TDS: use amount_applied as payment amount
          // For TDS: use payment_allocated as cash portion
          const paymentAmount = tdsAllocation.tds_not_applicable
            ? Math.round(alloc.amount_applied * 100) / 100
            : Math.round(alloc.payment_allocated * 100) / 100;
          effectiveNsinvoiceamount.push(paymentAmount);
          effectiveCreditsarray.push(Math.round((alloc.tds_amount || 0) * 100) / 100); // TDS credit (0 for non-TDS)
        }
        console.log('📨 [SEND MESSAGE] Using allocation data:', {
          tds_not_applicable: tdsAllocation.tds_not_applicable,
          effectiveNsinvoiceamount,
          effectiveCreditsarray
        });
      }

      // Build transaction schema based on agent type
      // bank_agent = Customer Payment (AR), bill_agent = Vendor Payment (AP), je_agent = Journal Entry
      const agentType = transaction.agent_type;
      let transactionSchema;

      if (agentType === 'je_agent') {
        // Journal Entry schema
        transactionSchema = {
          Slno: transaction.Slno || transaction.transaction_index || '',
          trandate: transaction.trandate || '',
          transactiontype: transaction.transactiontype || '',
          Amount: transaction.Amount || '',
          description: transaction.description || '',
          bank: transaction.bank || '',
          nsaccountid: transaction.nsaccountid || null,
          nsaccountname: transaction.nsaccountname || '',
          include: transaction.include !== false,
          ns_JE_id: transaction.ns_JE_id || [],
          ns_JE_doc: transaction.ns_JE_doc || [],
          viewje: transaction.viewje || ''
        };
      } else if (agentType === 'bill_agent') {
        // Vendor Payment (AP) schema - NO TDS fields
        // Build bill arrays from allocation if available (same as customer payments)
        let effectiveNsbillinternalid = transaction.nsbillinternalid || [];
        let effectiveNsbilldocnum = transaction.nsbilldocnum || [];
        let effectiveNsbillamount = transaction.nsbillamount || [];

        if (hasAllocation) {
          effectiveNsbillinternalid = [];
          effectiveNsbilldocnum = [];
          effectiveNsbillamount = [];
          for (const alloc of tdsAllocation.allocations) {
            effectiveNsbillinternalid.push(String(alloc.invoice_id));
            effectiveNsbilldocnum.push(alloc.invoice_number);
            // Vendor payments use amount_applied (no TDS)
            effectiveNsbillamount.push(Math.round(alloc.amount_applied * 100) / 100);
          }
          console.log('📨 [SEND MESSAGE] Using VENDOR allocation data:', {
            effectiveNsbillinternalid,
            effectiveNsbilldocnum,
            effectiveNsbillamount
          });
        }

        transactionSchema = {
          Amount: transaction.Amount || '',
          bank: transaction.bank || '',
          vendorname: transaction.vendorname || '',
          description: transaction.description || '',
          billlist: transaction.billlist || [],
          billnumber: transaction.billnumber || '',
          ns_billpay_doc: transaction.ns_billpay_doc || [],
          ns_billpayment_id: transaction.ns_billpayment_id || [],
          nsvendorinternalid: transaction.nsvendorinternalid || '',
          nsvendorname: transaction.nsvendorname || '',
          nsbillamount: effectiveNsbillamount,
          nsbilldocnum: effectiveNsbilldocnum,
          nsbillinternalid: effectiveNsbillinternalid,
          trandate: transaction.trandate || '',
          transactiontype: transaction.transactiontype || 'debit'
        };
      } else {
        // Customer Payment (AR) schema - WITH TDS support (bank_agent or default)
        transactionSchema = {
          Amount: transaction.Amount || '',
          bank: transaction.bank || '',
          customername: transaction.customername || '',
          description: transaction.description || '',
          invoicelist: transaction.invoicelist || [],
          invoicenumber: transaction.invoicenumber || '',
          ns_pay_doc: transaction.ns_pay_doc || [],
          ns_payment_id: transaction.ns_payment_id || [],
          JE_doc: transaction.JE_doc || [],
          JE_ID: transaction.JE_ID || [],
          nscustomerinternalid: transaction.nscustomerinternalid || '',
          nscustomername: transaction.nscustomername || '',
          ns_tds_percentage: tdsPercentage || transaction.ns_tds_percentage || '',
          nsinvoiceamount: effectiveNsinvoiceamount,
          nsinvoicedocnum: effectiveNsinvoicedocnum,
          nsinvoiceinternalid: effectiveNsinvoiceinternalid,
          creditsarray: effectiveCreditsarray,
          trandate: transaction.trandate || '',
          transactiontype: transaction.transactiontype || 'credit'
        };
      }

      // Always build the exact JSON structure for AI
      const messageForAI = {
        user_message: message,
        bankdata: transactionSchema,
        message_type: 'user_message'
      };

      let formattedMessage = JSON.stringify(messageForAI, null, 2);
      if (!transactionDataSent) {
        setTransactionDataSent(true);
      }

      // For JE Agent: Include jeLines with selected accounts, memos, department, class, location, customer, and vendor in the message
      if (transaction?.agent_type === 'je_agent' && jeLines.length > 0) {
        // Merge jeLineUpdates, jeLineMemos, jeLineDepartments, jeLineClasses, jeLineLocations, jeLineCustomers, jeLineVendors into jeLines for the message
        const jeLinesWithAccounts = jeLines.map((line, idx) => {
          const pendingAccountId = jeLineUpdates[idx];
          const lineMemo = jeLineMemos[idx];
          const lineDepartment = jeLineDepartments[idx];
          const lineClass = jeLineClasses[idx];
          const lineLocation = jeLineLocations[idx];
          const lineCustomer = jeLineCustomers[idx];
          const lineVendor = jeLineVendors[idx];
          let updatedLine = { ...line };

          if (pendingAccountId) {
            let accountName = pendingAccountId;
            Object.values(accountGroups).forEach(accounts => {
              const found = accounts.find(a => a.id === pendingAccountId);
              if (found) accountName = found.name;
            });
            updatedLine.nsaccountid = pendingAccountId;
            updatedLine.nsaccountname = accountName;
          }

          // Only include memo if user has entered a value
          if (lineMemo && lineMemo.trim()) {
            updatedLine.memo = lineMemo.trim();
          }

          // Only include department if user has selected a value
          if (lineDepartment) {
            updatedLine.department_id = lineDepartment;
            const deptObj = departmentList.find(d => d.id === lineDepartment);
            if (deptObj) updatedLine.department_name = deptObj.name;
          }

          // Only include class if user has selected a value
          if (lineClass) {
            updatedLine.class_id = lineClass;
            const classObj = classList.find(c => c.id === lineClass);
            if (classObj) updatedLine.class_name = classObj.name;
          }

          // Only include location if user has selected a value
          if (lineLocation) {
            updatedLine.location_id = lineLocation;
            const locObj = locationList.find(l => l.id === lineLocation);
            if (locObj) updatedLine.location_name = locObj.name;
          }

          // Only include customer if user has selected a value
          if (lineCustomer) {
            updatedLine.customer_id = lineCustomer;
            const custObj = customerList.find(c => c.value === lineCustomer);
            if (custObj) updatedLine.customer_name = custObj.label;
          }

          // Only include vendor if user has selected a value
          if (lineVendor) {
            updatedLine.vendor_id = lineVendor;
            const vendObj = vendorList.find(v => v.value === lineVendor);
            if (vendObj) updatedLine.vendor_name = vendObj.label;
          }

          return updatedLine;
        });

        formattedMessage += `\n\nJE Lines with selected accounts:\n${JSON.stringify(jeLinesWithAccounts, null, 2)}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/message/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          thread_id: currentThreadId,
          assistant_id: effectiveAssistantId,
          message: formattedMessage,
          transaction_context: {
            ...transaction,
            child_run_id: effectiveChildRunId
          }
        })
      });

      if (!response.ok) throw new Error('Failed to send message');
      return await response.json();
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    }
  };

  // Fetch payment details from NetSuite (used when payment timeout occurs)
  const fetchPaymentDetails = async () => {
    if (!childRunId || isFetchingPayment) return;

    // Determine payment type based on agent type
    const paymentType = transaction?.agent_type === 'bank_agent' ? 'customer' : 'vendor';

    setIsFetchingPayment(true);
    try {
      const response = await fetch('/api/bank-workflow-v2/fetch-payment-details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          child_run_id: childRunId,
          payment_type: paymentType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment details');
      }

      const result = await response.json();

      if (result.success && result.data) {
        if (result.data.payment_found) {
          // Payment found - add success message
          const successMsg = {
            id: Date.now(),
            type: 'assistant',
            content: `✅ **Payment Found in NetSuite!**\n\n**Document Number:** ${result.data.document_number}\n**Payment ID:** ${result.data.payment_id}\n**Amount:** ${result.data.payment_amount || 'N/A'}\n**Date:** ${result.data.payment_date || 'N/A'}\n\n[View Payment in NetSuite](${result.data.payment_url})`,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, successMsg]);
        } else {
          // Payment not found
          const notFoundMsg = {
            id: Date.now(),
            type: 'assistant',
            content: `❌ No payment found in NetSuite with the current workflow run ID. The payment may not have been created, or it was created with a different run ID.`,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, notFoundMsg]);
        }
      }
    } catch (err) {
      console.error('Error fetching payment details:', err);
      const errorMsg = {
        id: Date.now(),
        type: 'error',
        content: `Failed to fetch payment details: ${err.message}`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsFetchingPayment(false);
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    setIsLoading(true);

    // Build JSON payload for debug - matches prompt schema exactly
    // Use appropriate schema based on agent type (same logic as auto-trigger)
    const agentType = transaction?.agent_type;
    let txPayload;

    if (agentType === 'je_agent') {
      // Journal Entry schema
      txPayload = {
        Slno: transaction?.Slno || transaction?.transaction_index || '',
        trandate: transaction?.trandate || '',
        transactiontype: transaction?.transactiontype || '',
        Amount: transaction?.Amount || '',
        description: transaction?.description || '',
        bank: transaction?.bank || '',
        nsaccountid: transaction?.nsaccountid || null,
        nsaccountname: transaction?.nsaccountname || '',
        include: transaction?.include !== false,
        ns_JE_id: transaction?.ns_JE_id || [],
        ns_JE_doc: transaction?.ns_JE_doc || [],
        viewje: transaction?.viewje || ''
      };
    } else if (agentType === 'bill_agent') {
      // Vendor Payment (AP) schema - NO TDS fields
      txPayload = {
        Amount: transaction?.Amount || '',
        bank: transaction?.bank || '',
        vendorname: transaction?.vendorname || '',
        description: transaction?.description || '',
        billlist: transaction?.billlist || [],
        billnumber: transaction?.billnumber || '',
        ns_billpay_doc: transaction?.ns_billpay_doc || [],
        ns_billpayment_id: transaction?.ns_billpayment_id || [],
        nsvendorinternalid: transaction?.nsvendorinternalid || '',
        nsvendorname: transaction?.nsvendorname || '',
        nsbillamount: transaction?.nsbillamount || [],
        nsbilldocnum: transaction?.nsbilldocnum || [],
        nsbillinternalid: transaction?.nsbillinternalid || [],
        nsrequest_id: transaction?.nsrequest_id || '',
        nsrecord_type: transaction?.nsrecord_type || '',
        trandate: transaction?.trandate || '',
        transactiontype: transaction?.transactiontype || 'debit'
      };
    } else {
      // Customer Payment (AR) schema - WITH TDS support (bank_agent or default)
      txPayload = {
        Amount: transaction?.Amount || '',
        bank: transaction?.bank || '',
        customername: transaction?.customername || '',
        description: transaction?.description || '',
        invoicelist: transaction?.invoicelist || [],
        invoicenumber: transaction?.invoicenumber || '',
        ns_pay_doc: transaction?.ns_pay_doc || [],
        ns_payment_id: transaction?.ns_payment_id || [],
        JE_doc: transaction?.JE_doc || [],
        JE_ID: transaction?.JE_ID || [],
        nscustomerinternalid: transaction?.nscustomerinternalid || '',
        nscustomername: transaction?.nscustomername || '',
        ns_tds_percentage: tdsPercentage || transaction?.ns_tds_percentage || '',
        nsinvoiceamount: transaction?.nsinvoiceamount || [],
        nsinvoicedocnum: transaction?.nsinvoicedocnum || [],
        nsinvoiceinternalid: transaction?.nsinvoiceinternalid || [],
        creditsarray: transaction?.creditsarray || [],
        trandate: transaction?.trandate || '',
        transactiontype: transaction?.transactiontype || 'credit'
      };
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
      jsonPayload: { user_message: inputMessage, bankdata: txPayload, message_type: 'user_message' }
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    try {
      let currentChildWorkflowId = childWorkflowId;
      let currentChildRunId = childRunId;
      let currentAssistantId = assistantId;
      let currentThreadId = threadId;

      if (!currentChildWorkflowId) {
        const childWorkflowResult = await createChildWorkflow();
        if (!childWorkflowResult) throw new Error('Failed to create child workflow');
        currentChildWorkflowId = childWorkflowResult.workflowId;
        currentChildRunId = childWorkflowResult.runId;
        currentAssistantId = childWorkflowResult.assistantId;
        // Use existing thread_id if returned from existing workflow
        if (childWorkflowResult.threadId) {
          currentThreadId = childWorkflowResult.threadId;
        }
      }

      if (!currentThreadId) {
        currentThreadId = await createThread(currentAssistantId, currentChildRunId);
        if (!currentThreadId) throw new Error('Failed to create thread');
      }

      const response = await sendMessageToAssistant(messageToSend, currentThreadId, currentChildRunId, currentAssistantId);

      // DEBUG: Log raw response for right panel debugging
      console.log('🔍 [RIGHTPANEL DATA] Raw API response:', response);
      console.log('🔍 [RIGHTPANEL DATA] response.bankdata:', response.bankdata);
      console.log('🔍 [RIGHTPANEL DATA] response.response_json:', response.response_json);
      console.log('🔍 [RIGHTPANEL DATA] response.response_json?.bankdata:', response.response_json?.bankdata);
      if (response.bankdata && Array.isArray(response.bankdata)) {
        response.bankdata.forEach((item, idx) => {
          console.log(`🔍 [RIGHTPANEL DATA] bankdata[${idx}].billlist:`, item.billlist);
          console.log(`🔍 [RIGHTPANEL DATA] bankdata[${idx}].nsbillinternalid:`, item.nsbillinternalid);
        });
      }

      let assistantContent = response.response || response.message;
      let rawAiResponse = assistantContent; // Store raw response for debug
      let parsedAiJson = null; // Will store parsed JSON for debug
      let newTransaction = { ...transaction };

      // Extract payment info from payment_results (returned by backend) - do this first
      const paymentResults = response.payment_results || [];
      for (const paymentResult of paymentResults) {
        if (paymentResult.ns_billpayment_id && paymentResult.status === 'Success') {
          // Add vendor payment IDs to transaction
          newTransaction = {
            ...newTransaction,
            ns_billpayment_id: [...(newTransaction.ns_billpayment_id || []), paymentResult.ns_billpayment_id],
            ns_billpay_doc: [...(newTransaction.ns_billpay_doc || []), paymentResult.ns_billpay_doc]
          };
          console.log('💰 Vendor payment created:', paymentResult.ns_billpay_doc);
        }
        if (paymentResult.ns_payment_id && paymentResult.status === 'Success') {
          // Add customer payment IDs to transaction
          newTransaction = {
            ...newTransaction,
            ns_payment_id: [...(newTransaction.ns_payment_id || []), paymentResult.ns_payment_id],
            ns_pay_doc: [...(newTransaction.ns_pay_doc || []), paymentResult.ns_pay_doc]
          };
          console.log('💰 Customer payment created:', paymentResult.ns_pay_doc);
        }
        // Handle JE results - check both ns_je_id and ns_JE_id (backend uses capital JE)
        const jeId = paymentResult.ns_je_id || paymentResult.ns_JE_id;
        const jeDoc = paymentResult.ns_je_doc || paymentResult.ns_JE_doc;
        if (jeId && paymentResult.status === 'Success') {
          // Add journal entry IDs to transaction
          newTransaction = {
            ...newTransaction,
            ns_je_id: [...(newTransaction.ns_je_id || []), jeId],
            ns_je_doc: [...(newTransaction.ns_je_doc || []), jeDoc]
          };
          console.log('📒 Journal Entry created:', jeDoc);
        }
      }

      try {
        // Use response_json (enriched by backend with nsrequest_id, nsrecord_type) if available
        // Fall back to parsing response string if response_json not available
        const parsedResponse = response.response_json || JSON.parse(assistantContent);
        console.log('📊 [AI RESPONSE] Using enriched response_json:', !!response.response_json);
        parsedAiJson = parsedResponse; // Store for debug
        if (parsedResponse.user_message) {
          assistantContent = parsedResponse.user_message;
        }
        // Update transaction data if returned
        console.log('📊 [AI BANKDATA] parsedResponse.bankdata type:', typeof parsedResponse.bankdata, 'isArray:', Array.isArray(parsedResponse.bankdata));
        console.log('📊 [AI BANKDATA] parsedResponse.bankdata:', JSON.stringify(parsedResponse.bankdata)?.substring(0, 500));
        if (parsedResponse.bankdata) {
          const updatedTx = parsedResponse.bankdata[0] || parsedResponse.bankdata;
          console.log('📊 [AI BANKDATA] Raw bankdata from AI:', updatedTx);
          console.log('📊 [AI BANKDATA] billlist in updatedTx:', updatedTx.billlist?.length, updatedTx.billlist);
          console.log('📊 [AI BANKDATA] Payment fields:', {
            ns_billpayment_id: updatedTx.ns_billpayment_id,
            ns_billpay_doc: updatedTx.ns_billpay_doc,
            ns_payment_id: updatedTx.ns_payment_id,
            ns_pay_doc: updatedTx.ns_pay_doc
          });
          // Preserve manual_allocations (used by all agent types)
          const preservedAllocations = newTransaction.manual_allocations;
          // Preserve agent_type since AI bankdata might not include it
          const preservedAgentType = newTransaction.agent_type;
          newTransaction = { ...newTransaction, ...updatedTx };
          // Restore agent_type if AI didn't include it
          if (preservedAgentType && !updatedTx.agent_type) {
            newTransaction.agent_type = preservedAgentType;
          }
          // If we received billlist but agent_type not set, set it to bill_agent
          if (updatedTx.billlist?.length > 0 && !newTransaction.agent_type) {
            newTransaction.agent_type = 'bill_agent';
            console.log('📊 [AI BANKDATA] Auto-set agent_type to bill_agent based on billlist');
          }
          // If we received invoicelist but agent_type not set, set it to bank_agent
          if (updatedTx.invoicelist?.length > 0 && !newTransaction.agent_type) {
            newTransaction.agent_type = 'bank_agent';
            console.log('📊 [AI BANKDATA] Auto-set agent_type to bank_agent based on invoicelist');
          }
          // Restore manual_allocations if AI didn't include them
          if (preservedAllocations && !updatedTx.manual_allocations) {
            newTransaction.manual_allocations = preservedAllocations;
          }
          console.log('📊 [AI BANKDATA] After merge - newTransaction fields:', {
            agent_type: newTransaction.agent_type,
            nsrecord_type: newTransaction.nsrecord_type,
            billlist: newTransaction.billlist?.length,
            billlist_nsrecord_type: newTransaction.billlist?.[0]?.nsrecord_type,
            ns_billpayment_id: newTransaction.ns_billpayment_id,
            ns_billpay_doc: newTransaction.ns_billpay_doc,
            ns_payment_id: newTransaction.ns_payment_id,
            ns_pay_doc: newTransaction.ns_pay_doc
          });
          // For JE agent only: preserve account and line selections
          if (transaction?.agent_type === 'je_agent') {
            if (transaction.nsaccountid && !updatedTx.nsaccountid) {
              newTransaction.nsaccountid = transaction.nsaccountid;
              newTransaction.nsaccountname = transaction.nsaccountname;
              newTransaction.account_name = transaction.account_name;
            }
            if (transaction.je_lines && !updatedTx.je_lines) {
              newTransaction.je_lines = transaction.je_lines;
            }
            if (transaction.lines && !updatedTx.lines) {
              newTransaction.lines = transaction.lines;
            }
          }
        }
      } catch (e) {
        // Not JSON, use original
      }

      // Save updated transaction if we have payment results or bankdata
      if (paymentResults.length > 0 || newTransaction !== transaction) {
        console.log('🔢 [TDS DEBUG] Updated transaction ns_tds_percentage:', newTransaction.ns_tds_percentage);
        console.log('📊 [SEND MESSAGE] Calling setTransaction with billlist:', newTransaction.billlist?.length, 'nsrecord_type:', newTransaction.nsrecord_type);
        console.log('📊 [SEND MESSAGE] newTransaction.agent_type:', newTransaction.agent_type);
        console.log('📊 [SEND MESSAGE] Full newTransaction keys:', Object.keys(newTransaction));
        console.log('📊 [SEND MESSAGE] newTransaction.billlist:', newTransaction.billlist);
        setTransaction(newTransaction);
        console.log('📊 [SEND MESSAGE] ✅ setTransaction called');

        // Update TDS percentage state if present in updated transaction
        if (newTransaction.ns_tds_percentage && !tdsPercentage) {
          setTdsPercentage(newTransaction.ns_tds_percentage);
          console.log('🔢 [TDS DEBUG] Updated tdsPercentage from assistant response:', newTransaction.ns_tds_percentage);
        }

        // For vendor payments, explicitly calculate allocation after setting billlist
        // This ensures allocation panel updates immediately without waiting for useEffect
        if (newTransaction.agent_type === 'bill_agent' && newTransaction.billlist?.length > 0) {
          const paymentAmount = Math.abs(parseFloat(newTransaction.Amount) || 0);
          if (paymentAmount > 0) {
            console.log('📊 [SEND MESSAGE] Calculating vendor allocation immediately');
            const billlist = newTransaction.billlist;
            let remainingPayment = paymentAmount;
            const allocations = [];

            // Sort by due date (oldest first)
            const parseDate = (dateStr) => {
              if (!dateStr) return new Date('9999-12-31');
              const parts = dateStr.split('/');
              if (parts.length === 3) {
                return new Date(parts[2], parts[0] - 1, parts[1]);
              }
              return new Date(dateStr);
            };
            const sortedBills = [...billlist].sort((a, b) => parseDate(a.duedate) - parseDate(b.duedate));

            for (const bill of sortedBills) {
              if (remainingPayment <= 0) break;
              const billDue = Math.abs(parseFloat(bill.bill_total) || 0);
              if (billDue <= 0) continue;

              const paymentForBill = Math.min(remainingPayment, billDue);
              const billNum = bill.billnumber || bill.BillNumber;
              const billInternalId = bill.internalid;

              allocations.push({
                invoice_number: billNum,
                invoice_id: billInternalId || billNum,
                invoice_due: billDue,
                original_total: billDue,
                totpayamount: billDue,
                taxtotal: 0,
                expected_payment: Math.round(billDue * 100) / 100,
                payment_allocated: Math.round(paymentForBill * 100) / 100,
                tds_amount: 0,
                amount_applied: Math.round(paymentForBill * 100) / 100,
                is_partial: paymentForBill < billDue,
                is_manual: false
              });
              remainingPayment -= paymentForBill;
            }

            setTdsAllocation({
              allocations,
              total_tds: 0,
              total_payment: paymentAmount,
              tds_percentage: 0,
              is_manual: false,
              tds_not_applicable: true
            });
            console.log('📊 [SEND MESSAGE] Vendor allocation set:', allocations.length, 'bills');
          }
        }

        // Save transaction to database for persistence
        if (childRunId) {
          fetch(`/api/bank/v2/workflows/save-transaction/${childRunId}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ transaction: newTransaction })
          }).catch(err => console.warn('Failed to save transaction:', err));
        }
      }

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: assistantContent,
        timestamp: new Date().toISOString(),
        jsonPayload: parsedAiJson || rawAiResponse
      };

      setMessages(prev => [...prev, assistantMessage]);

      // ========== EXPLICIT RIGHT PANEL UPDATE ==========
      // Update transaction directly from API response for immediate right panel update
      console.log('🔄 [REALTIME] ===== CHECKING FOR BILLLIST/INVOICELIST =====');
      console.log('🔄 [REALTIME] response.bankdata:', JSON.stringify(response.bankdata)?.substring(0, 300));
      console.log('🔄 [REALTIME] response.response_json?.bankdata:', JSON.stringify(response.response_json?.bankdata)?.substring(0, 300));

      // Try multiple sources for bankdata
      let bankdataSource = response.bankdata || response.response_json?.bankdata;

      if (bankdataSource) {
        const bankdataItem = Array.isArray(bankdataSource) ? bankdataSource[0] : bankdataSource;
        console.log('🔄 [REALTIME] bankdataItem.billlist:', bankdataItem?.billlist);
        console.log('🔄 [REALTIME] bankdataItem.invoicelist:', bankdataItem?.invoicelist);

        if (bankdataItem && (bankdataItem.billlist?.length > 0 || bankdataItem.invoicelist?.length > 0)) {
          console.log('🔄 [REALTIME] ✅ Found bills/invoices, updating transaction state...');
          setTransaction(prev => ({
            ...prev,
            ...bankdataItem,
            agent_type: bankdataItem.agent_type || prev?.agent_type || (bankdataItem.billlist ? 'bill_agent' : 'bank_agent')
          }));
          console.log('🔄 [REALTIME] ✅ setTransaction called!');
        }
      }
      // ========== END EXPLICIT UPDATE ==========
    } catch (err) {
      console.error('Error in handleSendMessage:', err);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: `Error: ${err.message}`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  // Handle entity update
  const handleEntityUpdate = async () => {
    if (!selectedEntityId || !parentRunId) return;

    // Check similarEntities first, then allEntities
    const similarEntitiesList = getSimilarEntities();
    let selectedOption = similarEntitiesList.find(e => e.value === selectedEntityId);

    // If not found in similar, check allEntities
    if (!selectedOption) {
      selectedOption = allEntities.find(e => e.value === selectedEntityId);
    }

    if (!selectedOption) {
      console.error('Selected entity not found in any list');
      return;
    }

    // IMPORTANT: Correct entity type based on agent OR transaction type
    // bank_agent (AR) = customer, bill_agent (AP) = vendor, debit = vendor
    const isVendor = transaction.agent_type === 'bill_agent' || transaction.transactiontype === 'debit';
    const entityType = isVendor ? 'vendor' : 'customer';
    const noun = isVendor ? 'Vendor' : 'Customer';
    console.log('🔍 [ENTITY SELECT] Determining entity type:', {
      agent_type: transaction.agent_type,
      transactiontype: transaction.transactiontype,
      isVendor,
      entityType
    });

    setIsUpdatingEntity(true);

    // Add user message to chat
    const userMsg = `Please update ${noun} ${selectedOption.label} (nsinternalid="${selectedEntityId}") for ${getPredictedName()}.`;
    setMessages(prev => [...prev, { id: Date.now(), type: 'user', content: userMsg, timestamp: new Date().toISOString() }]);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/update-entity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: parentRunId,
          child_run_id: childRunId,
          transaction_index: transaction.transaction_index,
          transaction_description: transaction.description,  // For fast matching
          entity_type: entityType,
          entity_id: selectedEntityId,
          entity_name: selectedOption.label
        })
      });

      const data = await response.json();

      if (data.success) {
        // Build updated transaction with new entity info AND pre-fetched allocation data
        const updatedTransaction = {
          ...transaction,
          ...(entityType === 'customer'
            ? {
                nscustomerinternalid: selectedEntityId,
                nscustomername: selectedOption.label,
                // Include TDS percentage from backend if available
                ns_tds_percentage: data.ns_tds_percentage || selectedOption.ns_tds_percentage || transaction.ns_tds_percentage || '',
                // Include pre-fetched invoice allocation from backend
                invoicelist: data.invoicelist || transaction.invoicelist || [],
                nsinvoicedocnum: data.nsinvoicedocnum || transaction.nsinvoicedocnum || [],
                nsinvoiceamount: data.nsinvoiceamount || transaction.nsinvoiceamount || [],
                nsinvoiceinternalid: data.nsinvoiceinternalid || transaction.nsinvoiceinternalid || []
              }
            : {
                nsvendorinternalid: selectedEntityId,
                nsvendorname: selectedOption.label,
                // Include pre-fetched bill allocation from backend
                billlist: data.billlist || transaction.billlist || [],
                nsbilldocnum: data.nsbilldocnum || transaction.nsbilldocnum || [],
                nsbillamount: data.nsbillamount || transaction.nsbillamount || [],
                nsbillinternalid: data.nsbillinternalid || transaction.nsbillinternalid || []
              }
          )
        };
        setTransaction(updatedTransaction);

        // Update TDS percentage state if returned from backend (for customer)
        if (entityType === 'customer' && (data.ns_tds_percentage || selectedOption.ns_tds_percentage)) {
          const newTds = data.ns_tds_percentage || selectedOption.ns_tds_percentage;
          setTdsPercentage(newTds);
          console.log('🔢 [TDS DEBUG] Updated TDS from entity update:', newTds);
        }
        console.log('📊 [Entity Update] Updated transaction with allocation:', {
          invoices: updatedTransaction.nsinvoicedocnum,
          bills: updatedTransaction.nsbilldocnum
        });
        // Log full invoice/bill tool response from entity update
        if (data.invoicelist && data.invoicelist.length > 0) {
          console.log('📋 [ENTITY UPDATE - INVOICE TOOL RESPONSE] Full invoice data:', JSON.stringify(data.invoicelist, null, 2));
          data.invoicelist.forEach((inv, idx) => {
            console.log(`📋 [ENTITY UPDATE] Invoice ${idx + 1}:`, {
              InvoiceNumber: inv.InvoiceNumber || inv.invoicenumber,
              invoice_total: inv.invoice_total,
              totpayamount: inv.totpayamount,
              taxtotal: inv.taxtotal,
              nscustomername: inv.nscustomername
            });
          });
        }

        // Persist the updated transaction to database so it survives page refresh
        if (childRunId) {
          console.log('💾 [Entity Update] Saving updated transaction to database, childRunId:', childRunId);
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/save-transaction/${childRunId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ transaction: updatedTransaction })
          })
            .then(res => res.json())
            .then(saveResult => {
              console.log('💾 [Entity Update] Save result:', saveResult);
            })
            .catch(err => {
              console.warn('⚠️ [Entity Update] Failed to persist transaction:', err);
            });
        }

        // Show success message for entity update
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: `✅ ${noun} updated to ${selectedOption.label}`,
          timestamp: new Date().toISOString()
        }]);

        // Now trigger AI to fetch invoices/bills (same as Approve flow)
        // Need threadId and assistantId - create if not available
        let currentThreadId = threadId;
        let currentAssistantId = assistantId;

        // If no thread/assistant yet, we need to create them first
        if (!currentThreadId || !currentAssistantId) {
          console.log('🔧 [Entity Update] No thread/assistant - creating...');
          try {
            // First ensure we have assistant_id from child workflow
            if (!currentAssistantId && childRunId) {
              const runInfoRes = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/run-info/${childRunId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (runInfoRes.ok) {
                const runInfo = await runInfoRes.json();
                currentAssistantId = runInfo.assistant_id;
                if (currentAssistantId) setAssistantId(currentAssistantId);
              }
            }

            // Create thread if needed (use same metadata as createThread function)
            if (!currentThreadId && currentAssistantId) {
              const threadRes = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/thread/create`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  assistant_id: currentAssistantId,
                  metadata: {
                    transaction_type: transaction.agent_type,
                    transaction_index: transaction.transaction_index,
                    parent_workflow_id: parentWorkflowId,
                    parent_run_id: parentRunId,
                    child_workflow_id: childWorkflowId,
                    child_run_id: childRunId
                  }
                })
              });
              if (threadRes.ok) {
                const threadData = await threadRes.json();
                currentThreadId = threadData.thread_id;
                if (currentThreadId) setThreadId(currentThreadId);
              }
            }
          } catch (setupErr) {
            console.error('Failed to setup thread/assistant:', setupErr);
          }
        }

        if (currentThreadId && currentAssistantId) {
          const dataType = entityType === 'vendor' ? 'bills' : 'invoices';
          const autoMessage = `Show me the open ${dataType} for ${selectedOption.label}`;

          // Prepare transaction schema for AI based on entity type
          let txSchema;
          console.log('🔍 [TX SCHEMA] Building schema for entityType:', entityType, 'updatedTransaction.transactiontype:', updatedTransaction.transactiontype);

          if (entityType === 'vendor') {
            // Vendor Payment (AP) schema - NO TDS fields
            console.log('🔍 [TX SCHEMA] Using VENDOR schema');
            txSchema = {
              Amount: updatedTransaction.Amount || '',
              bank: updatedTransaction.bank || '',
              vendorname: updatedTransaction.vendorname || '',
              description: updatedTransaction.description || '',
              billlist: updatedTransaction.billlist || [],
              billnumber: updatedTransaction.billnumber || '',
              ns_billpay_doc: updatedTransaction.ns_billpay_doc || [],
              ns_billpayment_id: updatedTransaction.ns_billpayment_id || [],
              nsvendorinternalid: updatedTransaction.nsvendorinternalid || '',
              nsvendorname: updatedTransaction.nsvendorname || '',
              nsbillamount: [],
              nsbilldocnum: [],
              nsbillinternalid: [],
              trandate: updatedTransaction.trandate || '',
              transactiontype: updatedTransaction.transactiontype || 'debit'
            };
          } else {
            // Customer Payment (AR) schema - WITH TDS support (existing logic unchanged)
            console.log('🔍 [TX SCHEMA] Using CUSTOMER schema');
            txSchema = {
              Amount: updatedTransaction.Amount || '',
              bank: updatedTransaction.bank || '',
              customername: updatedTransaction.customername || '',
              description: updatedTransaction.description || '',
              invoicelist: updatedTransaction.invoicelist || [],
              invoicenumber: updatedTransaction.invoicenumber || '',
              ns_pay_doc: updatedTransaction.ns_pay_doc || [],
              ns_payment_id: updatedTransaction.ns_payment_id || [],
              JE_doc: updatedTransaction.JE_doc || [],
              JE_ID: updatedTransaction.JE_ID || [],
              nscustomerinternalid: updatedTransaction.nscustomerinternalid || '',
              nscustomername: updatedTransaction.nscustomername || '',
              ns_tds_percentage: updatedTransaction.ns_tds_percentage || '',
              nsinvoiceamount: [],
              nsinvoicedocnum: [],
              nsinvoiceinternalid: [],
              creditsarray: [],
              trandate: updatedTransaction.trandate || '',
              transactiontype: updatedTransaction.transactiontype || 'credit'
            };
          }

          // Build the exact JSON structure for AI
          const messageForAI = {
            user_message: autoMessage,
            bankdata: txSchema,
            message_type: 'user_message'
          };

          // Add user message to chat with JSON payload for debug
          setMessages(prev => [...prev, {
            id: `auto-${Date.now()}`,
            type: 'user',
            content: autoMessage,
            timestamp: new Date().toISOString(),
            jsonPayload: messageForAI
          }]);

          setIsLoading(true);

          // Send JSON string to AI
          const formattedMessage = JSON.stringify(messageForAI, null, 2);

          try {
            // Send to AI assistant
            const sendResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/message/send`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                thread_id: currentThreadId,
                assistant_id: currentAssistantId,
                message: formattedMessage,
                transaction_context: {
                  ...updatedTransaction,
                  child_run_id: childRunId
                }
              })
            });

            if (sendResponse.ok) {
              const respData = await sendResponse.json();
              let aiContent = respData.response || respData.message;
              let rawAiResponse = aiContent; // Store raw for debug

              // Extract user_message from JSON response if present
              let parsedAiJson = null;
              if (aiContent) {
                try {
                  parsedAiJson = typeof aiContent === 'string' ? JSON.parse(aiContent) : aiContent;
                  if (parsedAiJson.user_message) {
                    aiContent = parsedAiJson.user_message;
                  }
                } catch (e) {
                  // Not JSON, use as-is
                }
              }

              if (aiContent) {
                setMessages(prev => [...prev, {
                  id: `ai-${Date.now()}`,
                  type: 'assistant',
                  content: aiContent,
                  timestamp: new Date().toISOString(),
                  jsonPayload: parsedAiJson || rawAiResponse
                }]);
              }

              // Update transaction with bankdata from AI response (invoicelist, allocations, etc.)
              if (parsedAiJson && parsedAiJson.bankdata) {
                // Handle bankdata as array or object
                const aiBankdata = Array.isArray(parsedAiJson.bankdata)
                  ? parsedAiJson.bankdata[0]
                  : parsedAiJson.bankdata;
                console.log('📊 [AI RESPONSE - ENTITY] Updating transaction from AI bankdata:', aiBankdata);
                if (aiBankdata) {
                  setTransaction(prev => ({
                    ...prev,
                    invoicelist: aiBankdata.invoicelist || prev.invoicelist || [],
                    nsinvoicedocnum: aiBankdata.nsinvoicedocnum || [],
                    nsinvoiceamount: aiBankdata.nsinvoiceamount || [],
                    nsinvoiceinternalid: aiBankdata.nsinvoiceinternalid || [],
                    creditsarray: aiBankdata.creditsarray || [],
                    ns_tds_percentage: aiBankdata.ns_tds_percentage || prev.ns_tds_percentage || ''
                  }));
                }
              }
            }
          } catch (aiErr) {
            console.error('Failed to get AI response for invoices/bills:', aiErr);
          } finally {
            setIsLoading(false);
            setLoadingAction(null);
          }
        } else {
          console.warn('⚠️ [Entity Update] Could not create thread/assistant for AI trigger');
        }
      } else {
        throw new Error(data.error || 'Failed to update');
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Failed to update ${noun}: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsUpdatingEntity(false);
      setSelectedEntityId('');
    }
  };

  // Handle reallocation
  const handleReallocation = async () => {
    if (!enableReallocation || !parentRunId) return;

    const allocations = Object.entries(allocationAmounts)
      .filter(([_, amt]) => amt && parseFloat(amt) > 0)
      .map(([docNum, amt]) => ({ invoice_id: docNum, amount: parseFloat(amt) }));

    if (allocations.length === 0) {
      alert('❌ No valid allocations were entered.');
      return;
    }

    const docType = transaction.agent_type === 'bill_agent' ? 'Bill' : 'Invoice';
    const totalAllocated = allocations.reduce((s, a) => s + a.amount, 0);
    const transactionAmount = Math.abs(parseFloat(transaction.Amount) || 0);

    // Final validation: ensure total allocation doesn't exceed transaction amount
    if (totalAllocated > transactionAmount) {
      alert(`❌ Total allocation (${formatAmount(totalAllocated)}) exceeds payment amount (${formatAmount(transactionAmount)}). Please adjust your allocations.`);
      return;
    }

    setIsUpdatingAllocation(true);

    let userMsg = `Please process the following reallocations:\n\n`;
    userMsg += `| ${docType} Number | Allocated Amount |\n`;
    userMsg += `|------------------|------------------|\n`;
    allocations.forEach(a => {
      userMsg += `| ${a.invoice_id} | ${formatAmount(a.amount)} |\n`;
    });
    userMsg += `\nTotal: ${formatAmount(totalAllocated)}`;

    // Add user message to chat (NOT sent to AI - reallocation stays local)
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: userMsg,
      timestamp: new Date().toISOString(),
      jsonPayload: { note: 'Reallocation - NOT sent to AI', allocations }
    }]);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/update-allocation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: parentRunId,
          child_run_id: childRunId,
          transaction_index: transaction.transaction_index,
          allocations
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update transaction state with manual allocations AND update allocation arrays
        // This ensures subsequent messages to AI contain the correct allocations
        let updatedTransaction;

        if (transaction.agent_type === 'bill_agent') {
          // Vendor payment - use bill field names
          const nsbilldocnum = allocations.map(a => a.invoice_id);
          const nsbillamount = allocations.map(a => a.amount);
          // Find internal IDs from billlist - handle missing internalid gracefully
          const nsbillinternalid = allocations.map(a => {
            const bill = (transaction.billlist || []).find(
              b => (b.billnumber || b.BillNumber) === a.invoice_id
            );
            // Try bill.internalid first
            if (bill && bill.internalid) {
              return String(bill.internalid);
            }
            // If not found, look up from transaction's existing arrays
            if (transaction?.nsbilldocnum && transaction?.nsbillinternalid) {
              const idx = transaction.nsbilldocnum.indexOf(a.invoice_id);
              if (idx >= 0 && transaction.nsbillinternalid[idx]) {
                return String(transaction.nsbillinternalid[idx]);
              }
            }
            // Fallback to bill number itself (never return empty/undefined)
            return String(a.invoice_id);
          });

          updatedTransaction = {
            ...transaction,
            manual_allocations: allocations,
            allocation_modified: true,
            // Update allocation arrays so they're sent correctly to AI
            nsbilldocnum,
            nsbillamount,
            nsbillinternalid
          };
          console.log('📊 [REALLOCATION] Updated VENDOR transaction with new allocations:', {
            nsbilldocnum,
            nsbillamount,
            nsbillinternalid
          });
        } else {
          // Customer payment - use invoice field names
          const nsinvoicedocnum = allocations.map(a => a.invoice_id);
          const nsinvoiceamount = allocations.map(a => a.amount);
          // Find internal IDs from invoicelist - handle missing internalid gracefully
          const nsinvoiceinternalid = allocations.map(a => {
            const invoice = (transaction.invoicelist || []).find(
              inv => (inv.InvoiceNumber || inv.invoicenumber) === a.invoice_id
            );
            // Try invoice.internalid first
            if (invoice && invoice.internalid) {
              return String(invoice.internalid);
            }
            // If not found, look up from transaction's existing arrays
            if (transaction?.nsinvoicedocnum && transaction?.nsinvoiceinternalid) {
              const idx = transaction.nsinvoicedocnum.indexOf(a.invoice_id);
              if (idx >= 0 && transaction.nsinvoiceinternalid[idx]) {
                return String(transaction.nsinvoiceinternalid[idx]);
              }
            }
            // Fallback to invoice number itself (never return empty/undefined)
            return String(a.invoice_id);
          });

          updatedTransaction = {
            ...transaction,
            manual_allocations: allocations,
            allocation_modified: true,
            // Update allocation arrays so they're sent correctly to AI
            nsinvoicedocnum,
            nsinvoiceamount,
            nsinvoiceinternalid
          };
          console.log('📊 [REALLOCATION] Updated CUSTOMER transaction with new allocations:', {
            nsinvoicedocnum,
            nsinvoiceamount,
            nsinvoiceinternalid
          });
        }

        setTransaction(updatedTransaction);

        // Save transaction with allocations to database for persistence
        if (childRunId) {
          fetch(`/api/bank/v2/workflows/save-transaction/${childRunId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ transaction: updatedTransaction })
          }).catch(err => console.warn('Failed to save transaction with allocations:', err));
        }

        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: `✅ Allocations updated: ${allocations.length} ${docType.toLowerCase()}(s), total: ${formatAmount(totalAllocated)}`,
          timestamp: new Date().toISOString(),
          jsonPayload: { note: 'Reallocation confirmed - stored locally', allocations, total: totalAllocated }
        }]);
        setEnableReallocation(false);
        setAllocationAmounts({});
      } else {
        throw new Error(data.error || 'Failed to update');
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Failed to reallocate: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsUpdatingAllocation(false);
    }
  };

  // Handle Create Payment button click
  // Both TDS and non-TDS payments now go through AI assistant for chat continuity
  const handleCreatePayment = async () => {
    if (isLoading) return;
    setLoadingAction('create');

    // Check if allocation is available (works for both TDS and non-TDS)
    const hasAllocation = tdsAllocation &&
                          tdsAllocation.allocations &&
                          tdsAllocation.allocations.length > 0;

    const hasTds = hasAllocation && tdsAllocation.total_tds > 0 && !tdsNotApplicable;

    // Build updated JSON with allocations for AI assistant
    // Both TDS and non-TDS use this same flow now
    if (childRunId) {
      console.log('💰 [PAYMENT] Using AI assistant flow', hasTds ? '(with TDS)' : '(no TDS)', 'hasAllocation:', hasAllocation);

      try {
        setIsLoading(true);

        // Build arrays from allocation or existing transaction data
        let nsinvoiceinternalid = [];
        let nsinvoicedocnum = [];
        let nsinvoiceamount = []; // Payment amounts
        let creditsarray = []; // TDS credit amounts (empty for non-TDS)

        if (hasAllocation) {
          // Build arrays from allocation (works for both TDS and non-TDS)
          for (const alloc of tdsAllocation.allocations) {
            nsinvoiceinternalid.push(String(alloc.invoice_id));
            nsinvoicedocnum.push(alloc.invoice_number);
            // For non-TDS: use amount_applied as payment amount
            // For TDS: use payment_allocated as cash portion
            const paymentAmount = tdsAllocation.tds_not_applicable
              ? Math.round(alloc.amount_applied * 100) / 100
              : Math.round(alloc.payment_allocated * 100) / 100;
            nsinvoiceamount.push(paymentAmount);
            creditsarray.push(Math.round((alloc.tds_amount || 0) * 100) / 100); // TDS credit (0 for non-TDS)
          }
          console.log('💰 [PAYMENT] Built arrays from allocation:', {
            tds_not_applicable: tdsAllocation.tds_not_applicable,
            nsinvoiceinternalid,
            nsinvoicedocnum,
            nsinvoiceamount,
            creditsarray
          });
        } else {
          // Use existing transaction arrays (fallback)
          nsinvoiceinternalid = transaction?.nsinvoiceinternalid || [];
          nsinvoicedocnum = transaction?.nsinvoicedocnum || [];
          nsinvoiceamount = transaction?.nsinvoiceamount || [];
          creditsarray = []; // Empty for non-TDS
        }

        // Build the transaction JSON to send to AI based on agent type
        // Check for bill_agent OR debit transaction (vendor payment)
        const isVendorPayment = transaction.agent_type === 'bill_agent' || transaction.transactiontype === 'debit';
        let transactionForAI;

        if (isVendorPayment) {
          // Vendor Payment (AP) schema - NO TDS fields
          // Build arrays from billlist directly (includes bills AND credits)
          let nsbillinternalid = [];
          let nsbilldocnum = [];
          let nsbillamount = [];

          // Build arrays from billlist - include ALL items (bills + credits)
          const billlist = transaction?.billlist || [];
          if (billlist.length > 0) {
            for (const bill of billlist) {
              const billNum = bill.billnumber || bill.BillNumber;
              const billId = bill.internalid;
              const billAmount = parseFloat(bill.bill_total) || 0; // Keep negative for credits

              nsbillinternalid.push(String(billId));
              nsbilldocnum.push(billNum);
              nsbillamount.push(Math.round(billAmount * 100) / 100); // Keep sign (negative for credits)
            }
            console.log('💰 [PAYMENT] Built VENDOR arrays from billlist (with credits):', {
              nsbillinternalid,
              nsbilldocnum,
              nsbillamount,
              total: nsbillamount.reduce((sum, amt) => sum + amt, 0)
            });
          } else {
            // Fallback to transaction arrays
            nsbillinternalid = transaction?.nsbillinternalid || [];
            nsbilldocnum = transaction?.nsbilldocnum || [];
            nsbillamount = transaction?.nsbillamount || [];
          }

          // Get nsrequest_id and nsrecord_type from first bill
          const firstBill = billlist[0] || {};
          const nsrequest_id = firstBill.nsrequest_id || transaction.nsrequest_id || '';
          const nsrecord_type = firstBill.nsrecord_type || transaction.nsrecord_type || 'vendorPayment';

          transactionForAI = {
            Amount: transaction.Amount,
            bank: transaction.bank,
            vendorname: transaction.vendorname,
            description: transaction.description,
            billlist: billlist,
            billnumber: transaction.billnumber,
            ns_billpay_doc: transaction.ns_billpay_doc || [],
            ns_billpayment_id: transaction.ns_billpayment_id || [],
            nsvendorinternalid: transaction.nsvendorinternalid,
            nsvendorname: transaction.nsvendorname,
            nsbillamount: nsbillamount,
            nsbilldocnum: nsbilldocnum,
            nsbillinternalid: nsbillinternalid,
            nsrequest_id: nsrequest_id,
            nsrecord_type: nsrecord_type,
            trandate: transaction.trandate,
            transactiontype: transaction.transactiontype || 'debit'
          };
        } else {
          // Customer Payment (AR) schema - WITH TDS support (existing logic unchanged)
          transactionForAI = {
            Amount: transaction.Amount,
            bank: transaction.bank,
            customername: transaction.customername,
            description: transaction.description,
            invoicelist: transaction.invoicelist || [],
            invoicenumber: transaction.invoicenumber,
            ns_pay_doc: transaction.ns_pay_doc || [],
            ns_payment_id: transaction.ns_payment_id || [],
            JE_doc: transaction.JE_doc || [],
            JE_ID: transaction.JE_ID || [],
            nscustomerinternalid: transaction.nscustomerinternalid,
            nscustomername: transaction.nscustomername,
            ns_tds_percentage: tdsPercentage || transaction.ns_tds_percentage || '',
            nsinvoiceamount: nsinvoiceamount,
            nsinvoicedocnum: nsinvoicedocnum,
            nsinvoiceinternalid: nsinvoiceinternalid,
            creditsarray: creditsarray,
            trandate: transaction.trandate,
            transactiontype: transaction.transactiontype
          };
        }

        // Build user message
        const userMessageContent = hasTds
          ? `Create payment with TDS deduction`
          : `create payment`;

        // Ensure we have thread (do this before building messageForAI so we can add to chat after)
        let currentThreadId = threadId;
        let currentAssistantId = assistantId;
        let currentChildRunId = childRunId;

        if (!currentThreadId) {
          console.log('💰 [PAYMENT] No thread - creating...');
          currentThreadId = await createThread(currentAssistantId, currentChildRunId);
          if (!currentThreadId) {
            throw new Error('Failed to create thread');
          }
        }

        // Build the exact JSON structure for AI (with TDS allocation data)
        const messageForAI = {
          user_message: userMessageContent,
          bankdata: transactionForAI,
          message_type: 'user_message'
        };

        // Add user message to chat with JSON payload for debug
        const userMessage = {
          id: Date.now(),
          type: 'user',
          content: userMessageContent,
          timestamp: new Date().toISOString(),
          jsonPayload: messageForAI
        };
        setMessages(prev => [...prev, userMessage]);

        const messageToSend = JSON.stringify(messageForAI, null, 2);

        console.log('💰 [PAYMENT] Sending to AI assistant:', messageToSend.substring(0, 500) + '...');

        // Send directly to API (not through sendMessageToAssistant to preserve TDS allocation data)
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
        const apiResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/message/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            thread_id: currentThreadId,
            assistant_id: currentAssistantId,
            message: messageToSend,
            transaction_context: {
              ...transaction,
              child_run_id: currentChildRunId
            }
          })
        });

        if (!apiResponse.ok) throw new Error('Failed to send payment request');
        const response = await apiResponse.json();

        console.log('💰 [PAYMENT] AI Response:', response);

        // First extract payment IDs from payment_results (returned by backend when tool creates payment)
        // Handle both customer (ns_payment_id) and vendor (ns_billpayment_id) payments
        let extractedPaymentIds = [];
        let extractedPayDocs = [];
        let extractedBillPaymentIds = [];
        let extractedBillPayDocs = [];
        let extractedJeIds = [];
        let extractedJeDocs = [];

        const paymentResults = response.payment_results || [];
        for (const paymentResult of paymentResults) {
          // Customer payment
          if (paymentResult.ns_payment_id && paymentResult.status === 'Success') {
            extractedPaymentIds.push(paymentResult.ns_payment_id);
            extractedPayDocs.push(paymentResult.ns_pay_doc);
            console.log('💰 [PAYMENT] Extracted CUSTOMER payment from payment_results:', paymentResult.ns_pay_doc);
          }
          // Vendor payment
          if (paymentResult.ns_billpayment_id && paymentResult.status === 'Success') {
            extractedBillPaymentIds.push(paymentResult.ns_billpayment_id);
            extractedBillPayDocs.push(paymentResult.ns_billpay_doc);
            console.log('💰 [PAYMENT] Extracted VENDOR payment from payment_results:', paymentResult.ns_billpay_doc);
          }
          // Handle JE results
          const jeId = paymentResult.ns_je_id || paymentResult.ns_JE_id;
          const jeDoc = paymentResult.ns_je_doc || paymentResult.ns_JE_doc;
          if (jeId && paymentResult.status === 'Success') {
            extractedJeIds.push(jeId);
            extractedJeDocs.push(jeDoc);
            console.log('💰 [PAYMENT] Extracted JE from payment_results:', jeDoc);
          }
        }

        // Parse AI response
        let assistantContent = response.response || response.message;
        let rawAiResponse = assistantContent; // Store for debug
        let parsedAiJson = null;
        let parsedBankdata = null;

        try {
          // Use response_json (enriched by backend with nsrequest_id, nsrecord_type) if available
          const parsedResponse = response.response_json || JSON.parse(assistantContent);
          console.log('💰 [PAYMENT] Using enriched response_json:', !!response.response_json);
          parsedAiJson = parsedResponse; // Store for debug
          if (parsedResponse.user_message) {
            assistantContent = parsedResponse.user_message;
          }
          // Handle both object and array formats for bankdata
          if (parsedResponse.bankdata) {
            parsedBankdata = Array.isArray(parsedResponse.bankdata)
              ? parsedResponse.bankdata[0]
              : parsedResponse.bankdata;
          }
        } catch (e) {
          console.log('💰 [PAYMENT] Response is not JSON, using as-is');
        }

        // Merge extracted payment IDs with any from bankdata
        const finalPaymentIds = extractedPaymentIds.length > 0 ? extractedPaymentIds : (parsedBankdata?.ns_payment_id || []);
        const finalPayDocs = extractedPayDocs.length > 0 ? extractedPayDocs : (parsedBankdata?.ns_pay_doc || []);
        const finalBillPaymentIds = extractedBillPaymentIds.length > 0 ? extractedBillPaymentIds : (parsedBankdata?.ns_billpayment_id || []);
        const finalBillPayDocs = extractedBillPayDocs.length > 0 ? extractedBillPayDocs : (parsedBankdata?.ns_billpay_doc || []);
        const finalJeIds = extractedJeIds.length > 0 ? extractedJeIds : (parsedBankdata?.JE_ID || []);
        const finalJeDocs = extractedJeDocs.length > 0 ? extractedJeDocs : (parsedBankdata?.JE_doc || []);

        // Add assistant response to chat with JSON payload for debug
        const assistantMessage = {
          id: Date.now(),
          type: 'assistant',
          content: assistantContent,
          timestamp: new Date().toISOString(),
          jsonPayload: parsedAiJson || rawAiResponse
        };
        setMessages(prev => [...prev, assistantMessage]);

        // Update transaction state if we have payment IDs (from payment_results OR bankdata)
        const hasCustomerPayment = finalPaymentIds.length > 0;
        const hasVendorPayment = finalBillPaymentIds.length > 0;
        const hasJeIds = finalJeIds.length > 0;

        if (hasCustomerPayment || hasVendorPayment || hasJeIds) {
          // Determine payment type and build payment data
          const isVendor = isVendorPayment || hasVendorPayment;
          const paymentIds = isVendor ? finalBillPaymentIds : finalPaymentIds;
          const payDocs = isVendor ? finalBillPayDocs : finalPayDocs;
          // Use 'vprep' for vendor prepayment, 'vendpymt' for bill payment, 'custpymt' for customer payment
          // Check nsrecord_type, billlist, or if bill docs contain "Purchase Order"
          const billDocsForCheck = transaction.nsbilldocnum || [];
          const isPrepayment = transaction.nsrecord_type === 'vendorPrepayment'
            || transaction.billlist?.[0]?.nsrecord_type === 'vendorPrepayment'
            || billDocsForCheck.some(doc => doc && doc.includes('Purchase Order'));
          const recordType = isVendor
            ? (isPrepayment ? 'vprep' : 'vendpymt')
            : 'custpymt';

          // Set TDS payment state to hide Create Payment button
          const tdsPaymentData = {
            payment_id: paymentIds.length > 0 ? paymentIds[0] : 'created',
            payment_link: paymentIds.length > 0 ? getNetSuiteUrl(recordType, paymentIds[0]) : null,
            all_payments: paymentIds.map((id, idx) => ({
              payment_id: id,
              payment_doc: payDocs[idx] || `Payment ${id}`,
              payment_url: getNetSuiteUrl(recordType, id)
            })),
            created_jes: finalJeIds.map((id, idx) => ({
              je_id: id,
              je_doc: finalJeDocs[idx] || `JE ${id}`,
              je_url: getNetSuiteUrl('journal', id)
            }))
          };
          console.log('💰 [PAYMENT] Setting tdsPaymentCreated:', tdsPaymentData);
          setTdsPaymentCreated(tdsPaymentData);

          // Update transaction state with payment IDs based on type
          let updatedTx;
          if (isVendor) {
            updatedTx = {
              ...transaction,
              ns_billpayment_id: finalBillPaymentIds,
              ns_billpay_doc: finalBillPayDocs
            };
            setTransaction(prev => ({
              ...prev,
              ns_billpayment_id: finalBillPaymentIds,
              ns_billpay_doc: finalBillPayDocs
            }));
            console.log('💰 [PAYMENT] Updated VENDOR transaction with payment IDs:', { finalBillPaymentIds, finalBillPayDocs });
          } else {
            // Include nsinvoiceamount, nsinvoicedocnum, creditsarray for classification page
            updatedTx = {
              ...transaction,
              ns_payment_id: finalPaymentIds,
              ns_pay_doc: finalPayDocs,
              JE_ID: finalJeIds,
              JE_doc: finalJeDocs,
              nsinvoiceamount: nsinvoiceamount,
              nsinvoicedocnum: nsinvoicedocnum,
              nsinvoiceinternalid: nsinvoiceinternalid,
              creditsarray: creditsarray
            };
            setTransaction(prev => ({
              ...prev,
              ns_payment_id: finalPaymentIds,
              ns_pay_doc: finalPayDocs,
              JE_ID: finalJeIds,
              JE_doc: finalJeDocs,
              nsinvoiceamount: nsinvoiceamount,
              nsinvoicedocnum: nsinvoicedocnum,
              nsinvoiceinternalid: nsinvoiceinternalid,
              creditsarray: creditsarray
            }));
            console.log('💰 [PAYMENT] Updated CUSTOMER transaction with payment IDs:', { finalPaymentIds, finalPayDocs, finalJeIds, finalJeDocs, nsinvoiceamount, nsinvoicedocnum, creditsarray });
          }

          // Save to database for persistence (so classification page sees it)
          if (childRunId) {
            console.log('💾 [PAYMENT] Saving transaction with payment IDs to DB');
            fetch(`/api/bank/v2/workflows/save-transaction/${childRunId}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ transaction: updatedTx })
            }).catch(err => console.warn('Failed to save transaction after payment:', err));
          }
        }

      } catch (err) {
        console.error('💰 [PAYMENT] Error:', err);
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'error',
          content: `❌ Failed to create payment: ${err.message}`,
          timestamp: new Date().toISOString()
        }]);
      } finally {
        setIsLoading(false);
        setLoadingAction(null);
      }
    } else {
      // Fallback - no childRunId, use standard chat flow
      console.log('💰 [PAYMENT] No childRunId - using standard chat flow');
      setInputMessage('create payment');
      setTimeout(() => {
        document.getElementById('bank-chat-send-btn')?.click();
      }, 50);
    }
  };

  // Handle Check Created Payment button click - sends "check payment" message through existing chat flow
  const handleCheckPayment = () => {
    if (isLoading) return;
    setLoadingAction('check');
    setInputMessage('check payment');
    // Use setTimeout to ensure state is updated before triggering send
    setTimeout(() => {
      document.getElementById('bank-chat-send-btn')?.click();
    }, 50);
  };

  // Handle Create Journal Entry button click - simply sends "create journal entry" message through existing chat flow
  const handleCreateJE = () => {
    if (isLoading) return;
    setLoadingAction('je');
    setInputMessage('create journal entry');
    // Use setTimeout to ensure state is updated before triggering send
    setTimeout(() => {
      document.getElementById('bank-chat-send-btn')?.click();
    }, 50);
  };

  // Handle JE line selection toggle
  const handleJeLineSelect = (lineIndex) => {
    setSelectedJeLines(prev => ({
      ...prev,
      [lineIndex]: !prev[lineIndex]
    }));
  };

  // Handle JE line account change
  const handleJeAccountChange = (lineIndex, accountId) => {
    setJeLineUpdates(prev => ({
      ...prev,
      [lineIndex]: accountId
    }));
  };

  // Handle apply JE updates (account, memo, department, class, location)
  const handleApplyJeUpdates = async () => {
    // Collect all line indices that have any changes
    const allLineIndices = new Set([
      ...Object.keys(jeLineUpdates),
      ...Object.keys(jeLineMemos),
      ...Object.keys(jeLineDepartments),
      ...Object.keys(jeLineClasses),
      ...Object.keys(jeLineLocations)
    ]);

    const updates = [];
    const updateSummaryParts = [];

    allLineIndices.forEach(lineIndex => {
      const idx = parseInt(lineIndex);
      const line = jeLines[idx] || {};

      const accountId = jeLineUpdates[idx];
      const memo = jeLineMemos[idx];
      const deptId = jeLineDepartments[idx];
      const classId = jeLineClasses[idx];
      const locId = jeLineLocations[idx];

      // Only include if there's at least one change
      if (!accountId && !memo && !deptId && !classId && !locId) return;

      // Use existing account if no new account selected
      const finalAccountId = accountId || line.nsaccountid || line.account_id;

      if (!finalAccountId) {
        // Skip lines without any account (either new or existing)
        return;
      }

      updates.push({
        line_index: idx,
        new_account_id: finalAccountId,
        memo: memo || '',
        department_id: deptId || '',
        class_id: classId || '',
        location_id: locId || ''
      });

      // Build summary
      const parts = [`Line ${idx + 1}:`];
      if (accountId) parts.push(`Account → ${accountId}`);
      if (memo) parts.push(`Memo → ${memo}`);
      if (deptId) parts.push(`Dept → ${deptId}`);
      if (classId) parts.push(`Class → ${classId}`);
      if (locId) parts.push(`Location → ${locId}`);
      updateSummaryParts.push(parts.join(' '));
    });

    if (updates.length === 0) {
      alert('No changes to apply. Please select account, memo, department, class, or location for at least one line.');
      return;
    }

    setIsApplyingJeUpdates(true);

    // Add user message showing update
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: `Applying updates:\n${updateSummaryParts.join('\n')}`,
      timestamp: new Date().toISOString()
    }]);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/update-je-lines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: parentRunId,
          transaction_index: transaction.transaction_index,
          updates
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update local state with all changes
        setJeLines(prev => prev.map((line, idx) => {
          const update = updates.find(u => u.line_index === idx);
          if (update) {
            // Find account name from groups
            let accountName = update.new_account_id;
            Object.values(accountGroups).forEach(accounts => {
              const found = accounts.find(a => a.id === update.new_account_id);
              if (found) accountName = found.name;
            });
            return {
              ...line,
              new_account_id: update.new_account_id,
              nsaccountid: update.new_account_id,
              nsaccountname: accountName,
              account_name: accountName,
              memo: update.memo !== undefined && update.memo !== '' ? update.memo : line.memo,
              department_id: update.department_id !== undefined && update.department_id !== '' ? update.department_id : line.department_id,
              class_id: update.class_id !== undefined && update.class_id !== '' ? update.class_id : line.class_id,
              location_id: update.location_id !== undefined && update.location_id !== '' ? update.location_id : line.location_id
            };
          }
          return line;
        }));

        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: `✅ Applied ${updates.length} update(s) successfully.`,
          timestamp: new Date().toISOString()
        }]);

        // Clear all pending updates
        setJeLineUpdates({});
        setJeLineMemos({});
        setJeLineDepartments({});
        setJeLineClasses({});
        setJeLineLocations({});
        setSelectedJeLines({});
      } else {
        throw new Error(data.error || 'Failed to apply updates');
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Failed to apply updates: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsApplyingJeUpdates(false);
    }
  };

  // Handle mass update for selected JE lines
  const handleJeMassUpdate = () => {
    if (!jeMassUpdateAccountId) {
      alert('Please select an account for mass update');
      return;
    }

    const selectedIndices = Object.entries(selectedJeLines)
      .filter(([_, selected]) => selected)
      .map(([idx, _]) => parseInt(idx));

    if (selectedIndices.length === 0) {
      alert('Please select at least one line to update');
      return;
    }

    // Apply mass update to all selected lines
    const newUpdates = {};
    selectedIndices.forEach(idx => {
      newUpdates[idx] = jeMassUpdateAccountId;
    });

    setJeLineUpdates(prev => ({ ...prev, ...newUpdates }));
    setJeMassUpdateEnabled(false);
    setJeMassUpdateAccountId('');
  };

  // Get pending JE updates for preview (account, memo, department, class, location)
  const getPendingJeUpdates = () => {
    const updates = [];

    // Collect all line indices that have any changes
    const allLineIndices = new Set([
      ...Object.keys(jeLineUpdates),
      ...Object.keys(jeLineMemos),
      ...Object.keys(jeLineDepartments),
      ...Object.keys(jeLineClasses),
      ...Object.keys(jeLineLocations),
      ...Object.keys(jeLineCustomers),
      ...Object.keys(jeLineVendors)
    ]);

    allLineIndices.forEach(lineIndex => {
      const idx = parseInt(lineIndex);
      const line = jeLines[idx] || {};

      // Skip if JE already created for THIS specific line
      if (line.viewje || line.je_id || line.ns_je_id) {
        return;
      }

      const accountId = jeLineUpdates[idx];
      const memo = jeLineMemos[idx];
      const deptId = jeLineDepartments[idx];
      const classId = jeLineClasses[idx];
      const locId = jeLineLocations[idx];
      const custId = jeLineCustomers[idx];
      const vendId = jeLineVendors[idx];

      // Only include if there's at least one change
      if (!accountId && !memo && !deptId && !classId && !locId && !custId && !vendId) return;

      // Find account name from groups
      let accountName = accountId || '';
      if (accountId) {
        Object.values(accountGroups).forEach(accounts => {
          const found = accounts.find(a => a.id === accountId);
          if (found) accountName = found.name;
        });
      }

      // Find department, class, location, customer, vendor names
      const deptName = deptId ? (departmentList.find(d => d.id === deptId)?.name || deptId) : '';
      const className = classId ? (classList.find(c => c.id === classId)?.name || classId) : '';
      const locName = locId ? (locationList.find(l => l.id === locId)?.name || locId) : '';
      const custName = custId ? (customerList.find(c => c.value === custId)?.label || custId) : '';
      const vendName = vendId ? (vendorList.find(v => v.value === vendId)?.label || vendId) : '';

      updates.push({
        lineIndex: idx,
        description: line.description || line.memo || `Line ${idx + 1}`,
        amount: formatAmount(line.amount || line.debit || line.credit || 0),
        newAccountId: accountId || '',
        newAccountName: accountName,
        memo: memo || '',
        deptId: deptId || '',
        deptName: deptName,
        classId: classId || '',
        className: className,
        locId: locId || '',
        locName: locName,
        custId: custId || '',
        custName: custName,
        vendId: vendId || '',
        vendName: vendName
      });
    });

    return updates.sort((a, b) => a.lineIndex - b.lineIndex);
  };

  // Handle transfer to JE
  const handleTransferToJE = async () => {
    if (!parentRunId) return;

    const confirmMsg = `Transfer this transaction (${formatAmount(transaction.Amount)} - ${getPredictedName()}) to JE Agent?`;
    if (!window.confirm(confirmMsg)) return;

    setIsTransferringToJE(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/transfer-to-je`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: parentRunId,
          transaction_index: transaction.transaction_index
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update local transaction state
        const updatedTransaction = {
          ...transaction,
          agent_type: 'je_agent',
          transferred_to_je: true,
          original_agent_type: data.original_type || transaction.agent_type
        };
        setTransaction(updatedTransaction);

        // Save updated transaction to child workflow for persistence
        if (childRunId) {
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/save-transaction/${childRunId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ transaction: updatedTransaction })
          }).catch(err => console.warn('Failed to save JE transfer:', err));
        }

        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: `✅ Successfully transferred transaction to JE Agent! The right panel will now show Journal Entry options.`,
          timestamp: new Date().toISOString()
        }]);
      } else {
        throw new Error(data.error || 'Failed to transfer');
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Transfer failed: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsTransferringToJE(false);
    }
  };

  const handleBack = () => {
    // Navigate back to BankChatHistory with openRun param to auto-open TransactionsModal
    if (parentRunId) {
      navigate(`/bank-chat-history?openRun=${parentRunId}`);
    } else {
      navigate(-1);
    }
  };

  const handleDebug = () => {
    const params = new URLSearchParams();
    if (parentRunId) params.set('parentRun', parentRunId);
    if (childRunId) params.set('childRun', childRunId);
    navigate(`/bank-debug?${params.toString()}`);
  };

  const getBadgeColor = () => {
    switch (transaction?.agent_type) {
      case 'bank_agent': return 'bg-emerald-500';
      case 'bill_agent': return 'bg-amber-500';
      default: return 'bg-indigo-500';
    }
  };

  // Check if any allocation is valid
  const isAnyAllocationValid = enableReallocation &&
    Object.values(allocationAmounts).some(v => v && parseFloat(v) > 0);

  if (error) {
    return (
      <MainLayout title="Bank Agent Chat">
        <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
          <div className="text-center p-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
            <h2 className="text-red-600 dark:text-red-400 text-xl font-semibold mb-3">Error</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button onClick={handleBack} className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all">
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!transaction) {
    return (
      <MainLayout title="Bank Agent Chat">
        <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] gap-4">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-indigo-500 rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">Loading transaction...</p>
        </div>
      </MainLayout>
    );
  }

  const openDocs = getOpenDocs();
  const suggestedAlloc = getSuggestedAllocation();
  const paymentLinks = getPaymentLinks();
  const similarEntities = getSimilarEntities();
  const entityNoun = transaction.agent_type === 'bill_agent' ? 'Vendor' : 'Customer';
  const docNoun = transaction.agent_type === 'bill_agent' ? 'Bills' : 'Invoices';

  return (
    <MainLayout title="Bank Agent Chat">
      <div className="flex flex-col h-[calc(100vh-8rem)] bg-white dark:bg-gray-900 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-4 px-6 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <button onClick={handleBack} className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 flex-1">
            <div className="relative p-2.5 bg-white/20 rounded-xl">
              <Bot className="w-7 h-7 text-white" />
              <div className="absolute -bottom-1 -right-1 p-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full">
                <Sparkles className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-semibold">{getTransactionTypeName(transaction.agent_type)} Approval</h1>
              <p className="text-xs opacity-90">Transaction #{transaction.transaction_index + 1}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {(parentRunId || childRunId) && (
              <button onClick={handleDebug} className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-all" title="View Execution Logs">
                <Bug className="w-5 h-5" />
              </button>
            )}
            <div className={`px-3 py-1.5 ${getBadgeColor()} rounded-full text-xs font-semibold`}>
              {transaction.agent_type === 'bank_agent' ? 'AR' : transaction.agent_type === 'bill_agent' ? 'AP' : 'JE'}
            </div>
          </div>
        </div>

        {/* Main Content - Two Panel Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT: Chat Panel */}
          <div className="flex-1 flex flex-col border-r border-gray-200 dark:border-gray-700">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-800 flex flex-col gap-3">
              {messages.map((message) => (
                <React.Fragment key={message.id}>
                <div
                  className={`flex gap-2 max-w-[85%] ${
                    message.type === 'user' ? 'self-end flex-row-reverse' :
                    message.type === 'error' ? 'self-center' : 'self-start'
                  }`}
                >
                  {message.type === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white flex-shrink-0">
                      <Bot className="w-4 h-4" />
                    </div>
                  )}
                  <div className={`px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                    message.type === 'assistant' ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600' :
                    message.type === 'user' ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white' :
                    'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700'
                  }`}>
                    {/* Render HTML for assistant messages containing links, plain text for others */}
                    {message.type === 'assistant' && message.content && message.content.includes('<a ') ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(formatMessageContent(message.content).replace(/\n/g, '<br/>').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')) }} />
                    ) : (
                      formatMessageContent(message.content)
                    )}
                    {/* Show Fetch Payment Details button for timeout/error messages related to payments */}
                    {(message.type === 'error' || message.type === 'assistant') &&
                     message.content &&
                     (message.content.toLowerCase().includes('timeout') ||
                      message.content.toLowerCase().includes('retry payment') ||
                      message.content.toLowerCase().includes('payment failed') ||
                      message.content.toLowerCase().includes('attempt to retry')) &&
                     transaction?.agent_type !== 'je_agent' && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                        <button
                          onClick={fetchPaymentDetails}
                          disabled={isFetchingPayment || !childRunId}
                          className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white text-xs rounded-md flex items-center gap-1"
                        >
                          {isFetchingPayment ? (
                            <>
                              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              Fetching...
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              Fetch Payment Details
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Admin Debug Icon - Show JSON payload */}
                  {isAdmin && (
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => setExpandedDebugId(expandedDebugId === message.id ? null : message.id)}
                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                          message.jsonPayload
                            ? 'bg-green-200 dark:bg-green-700 hover:bg-green-300 dark:hover:bg-green-600 text-green-700 dark:text-green-200'
                            : 'bg-red-200 dark:bg-red-700 hover:bg-red-300 dark:hover:bg-red-600 text-red-700 dark:text-red-200'
                        }`}
                        title={message.jsonPayload ? (message.type === 'user' ? 'View JSON sent to AI' : 'View JSON response from AI') : 'No JSON payload'}
                      >
                        <Bug className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                {/* Expanded JSON Debug Panel */}
                {isAdmin && expandedDebugId === message.id && (
                  <div className={`mt-1 max-w-[90%] ${message.type === 'user' ? 'self-end' : 'self-start ml-10'}`}>
                    <div className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs font-mono overflow-x-auto max-h-[300px] overflow-y-auto border border-gray-600">
                      <div className="text-gray-400 mb-2 text-[10px] uppercase tracking-wide">
                        {message.type === 'user' ? '📤 JSON Sent to AI:' : '📥 JSON Response from AI:'}
                      </div>
                      {message.jsonPayload ? (
                        <pre className="whitespace-pre-wrap break-words">
                          {JSON.stringify(message.jsonPayload, null, 2)}
                        </pre>
                      ) : (
                        <div className="text-red-400">⚠️ No JSON payload stored for this message</div>
                      )}
                    </div>
                  </div>
                )}
                </React.Fragment>
              ))}

              {isLoading && (
                <div className="flex gap-2 self-start">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-1 px-3 py-2 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="flex gap-2 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm outline-none focus:border-indigo-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <button
                id="bank-chat-send-btn"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* RIGHT: Transaction Status Panel */}
          <div className="w-[450px] flex-shrink-0 overflow-y-auto bg-white dark:bg-gray-900 p-4">
            {/* ======================== JE AGENT PANEL ======================== */}
            {transaction.agent_type === 'je_agent' ? (
              <>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                  📋 Journal Entry Lines
                </h3>

                {/* Loading Accounts */}
                {isLoadingAccounts && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin"></div>
                    Loading accounts...
                  </div>
                )}

                {/* JE Lines Table */}
                {!isLoadingAccounts && (
                  <div className="space-y-4">
                    {/* Transaction Details Summary */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm space-y-1">
                      <div><span className="font-medium">Amount:</span> {formatAmount(transaction.Amount)}</div>
                      <div><span className="font-medium">Date:</span> {transaction.trandate}</div>
                      <div><span className="font-medium">Bank:</span> {transaction.bank || 'N/A'}</div>
                      {transaction.transferred_to_je && transaction.original_agent_type && (
                        <div className="text-amber-600 dark:text-amber-400">
                          <span className="font-medium">Transferred from:</span> {transaction.original_agent_type === 'bank_agent' ? 'AR (Customer Payment)' : 'AP (Vendor Payment)'}
                        </div>
                      )}
                      {(transaction.customername || transaction.vendorname) && (
                        <div>
                          <span className="font-medium">{transaction.customername ? 'Customer' : 'Vendor'}:</span> {transaction.customername || transaction.vendorname}
                        </div>
                      )}
                      <div><span className="font-medium">Description:</span> <span className="break-words">{transaction.description}</span></div>
                    </div>

                    {/* JE Lines Table - Responsive with horizontal scroll */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs min-w-[650px]">
                          <thead className="bg-gray-100 dark:bg-gray-800">
                            <tr>
                              <th className="px-2 py-2 text-left w-8">
                                <input
                                  type="checkbox"
                                  onChange={(e) => {
                                    const newSelected = {};
                                    jeLines.forEach((_, idx) => {
                                      newSelected[idx] = e.target.checked;
                                    });
                                    setSelectedJeLines(newSelected);
                                  }}
                                  checked={jeLines.length > 0 && Object.values(selectedJeLines).filter(Boolean).length === jeLines.length}
                                  className="w-3.5 h-3.5 rounded"
                                />
                              </th>
                              <th className="px-2 py-2 text-left w-10">#</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Description</th>
                              <th className="px-2 py-2 text-left w-12">Type</th>
                              <th className="px-2 py-2 text-right w-24">Amount</th>
                              <th className="px-2 py-2 text-left min-w-[140px]">Select Account</th>
                              <th className="px-2 py-2 text-left min-w-[120px]">Customer</th>
                              <th className="px-2 py-2 text-left min-w-[120px]">Vendor</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Memo</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Dept</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Class</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Location</th>
                              <th className="px-2 py-2 text-left w-16">JE</th>
                            </tr>
                          </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {jeLines.length === 0 ? (
                            <tr>
                              <td colSpan="13" className="px-4 py-6 text-center text-gray-500">
                                No journal entry lines available
                              </td>
                            </tr>
                          ) : (
                            jeLines.map((line, idx) => {
                              // Check if JE already created - check both transaction level and line level
                              // transaction.ns_JE_id/ns_je_id = transaction level, line.viewje/je_id/ns_je_id = line level
                              const jeCreated = !!(transaction.ns_JE_id?.[0] || transaction.ns_je_id?.[0] || line.viewje || line.je_id || line.ns_je_id);

                              // Check if there's a pending update for this line
                              const pendingAccountId = jeLineUpdates[idx];
                              let displayLabel = line.nsaccountname || line.account_name || line.current_account || '';
                              let displayId = line.nsaccountid || '';

                              // If there's a pending selection, show that instead
                              if (pendingAccountId) {
                                displayId = pendingAccountId;
                                // Find the account name from groups
                                Object.values(accountGroups).forEach(accounts => {
                                  const found = accounts.find(a => a.id === pendingAccountId);
                                  if (found) displayLabel = found.name;
                                });
                              }

                              const currentLabel = displayLabel;
                              const currentId = displayId;
                              return (
                                <tr key={idx} className={`${selectedJeLines[idx] ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''} ${jeCreated ? 'bg-green-50 dark:bg-green-900/20' : ''} hover:bg-gray-50 dark:hover:bg-gray-800`}>
                                  <td className="px-2 py-2">
                                    <input
                                      type="checkbox"
                                      checked={!!selectedJeLines[idx]}
                                      onChange={() => handleJeLineSelect(idx)}
                                      disabled={jeCreated}
                                      className="w-3.5 h-3.5 rounded"
                                    />
                                  </td>
                                  <td className="px-2 py-2 text-gray-600 dark:text-gray-400">{idx + 1}</td>
                                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[100px]" title={line.description || line.memo}>
                                    {line.description || line.memo || '-'}
                                  </td>
                                  <td className="px-2 py-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                      line.type === 'debit' || line.credit === 0
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                    }`}>
                                      {line.type === 'debit' || line.credit === 0 ? 'DR' : 'CR'}
                                    </span>
                                  </td>
                                  <td className="px-2 py-2 text-right font-mono text-gray-700 dark:text-gray-300">
                                    {formatAmount(line.amount || line.debit || line.credit)}
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineUpdates[idx] || ''}
                                      onChange={(e) => handleJeAccountChange(idx, e.target.value)}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">{currentLabel ? `-- Keep (${currentLabel}) --` : '-- Select --'}</option>
                                      {Object.entries(accountGroups).map(([groupName, accounts]) => (
                                        <optgroup key={groupName} label={groupName}>
                                          {accounts.map(acct => (
                                            <option key={acct.id} value={acct.id}>
                                              {acct.name}
                                            </option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineCustomers[idx] || line.customer_id || ''}
                                      onChange={(e) => setJeLineCustomers(prev => ({ ...prev, [idx]: e.target.value }))}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">-- Select Customer --</option>
                                      {customerList.map(cust => (
                                        <option key={cust.value} value={cust.value}>{cust.label}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineVendors[idx] || line.vendor_id || ''}
                                      onChange={(e) => setJeLineVendors(prev => ({ ...prev, [idx]: e.target.value }))}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">-- Select Vendor --</option>
                                      {vendorList.map(vend => (
                                        <option key={vend.value} value={vend.value}>{vend.label}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    <input
                                      type="text"
                                      value={jeLineMemos[idx] !== undefined ? jeLineMemos[idx] : (line.memo || '')}
                                      onChange={(e) => setJeLineMemos(prev => ({ ...prev, [idx]: e.target.value }))}
                                      placeholder="Memo..."
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    />
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineDepartments[idx] || line.department_id || ''}
                                      onChange={(e) => setJeLineDepartments(prev => ({ ...prev, [idx]: e.target.value }))}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">-- Select Dept --</option>
                                      {departmentList.map(dept => (
                                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineClasses[idx] || line.class_id || ''}
                                      onChange={(e) => setJeLineClasses(prev => ({ ...prev, [idx]: e.target.value }))}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">-- Select Class --</option>
                                      {classList.map(cls => (
                                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineLocations[idx] || line.location_id || ''}
                                      onChange={(e) => setJeLineLocations(prev => ({ ...prev, [idx]: e.target.value }))}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">-- Select Location --</option>
                                      {locationList.map(loc => (
                                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    {/* Show created JE link from transaction level (ns_je_id/ns_JE_id) or line level (viewje) */}
                                    {(transaction.ns_JE_id?.[0] || transaction.ns_je_id?.[0] || line.viewje) ? (
                                      <a
                                        href={getNetSuiteUrl('journal', transaction.ns_JE_id?.[0] || transaction.ns_je_id?.[0] || line.viewje)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800 hover:underline text-xs"
                                      >
                                        {transaction.ns_JE_doc?.[0] || transaction.ns_je_doc?.[0] || 'View JE'}
                                      </a>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pending Updates Preview - hide after JE is created */}
                    {paymentLinks.length === 0 && getPendingJeUpdates().length > 0 && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                        <div className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                          📝 Pending Updates ({getPendingJeUpdates().length} line(s))
                        </div>
                        <div className="space-y-2">
                          {getPendingJeUpdates().map((update, idx) => (
                            <div key={idx} className="text-xs text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 rounded p-2">
                              <div className="font-semibold mb-1">Line {update.lineIndex + 1} - {update.amount}</div>
                              <div className="grid grid-cols-2 gap-1 text-[10px]">
                                {update.newAccountName && (
                                  <div><span className="text-yellow-600">Account:</span> <span className="text-green-600 dark:text-green-400">{update.newAccountName}</span></div>
                                )}
                                {update.memo && (
                                  <div><span className="text-yellow-600">Memo:</span> <span className="text-green-600 dark:text-green-400">{update.memo}</span></div>
                                )}
                                {update.deptName && (
                                  <div><span className="text-yellow-600">Dept:</span> <span className="text-green-600 dark:text-green-400">{update.deptName}</span></div>
                                )}
                                {update.className && (
                                  <div><span className="text-yellow-600">Class:</span> <span className="text-green-600 dark:text-green-400">{update.className}</span></div>
                                )}
                                {update.locName && (
                                  <div><span className="text-yellow-600">Location:</span> <span className="text-green-600 dark:text-green-400">{update.locName}</span></div>
                                )}
                                {update.custName && (
                                  <div><span className="text-yellow-600">Customer:</span> <span className="text-green-600 dark:text-green-400">{update.custName}</span></div>
                                )}
                                {update.vendName && (
                                  <div><span className="text-yellow-600">Vendor:</span> <span className="text-green-600 dark:text-green-400">{update.vendName}</span></div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Mass Update Section - hide after JE is created */}
                    {paymentLinks.length === 0 && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <div className="flex items-center gap-3 mb-2">
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={jeMassUpdateEnabled}
                            onChange={(e) => setJeMassUpdateEnabled(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <span className="font-medium">Mass Update</span>
                        </label>
                        {jeMassUpdateEnabled && (
                          <span className="text-xs text-gray-500">
                            ({Object.values(selectedJeLines).filter(Boolean).length} line(s) selected)
                          </span>
                        )}
                      </div>

                      {jeMassUpdateEnabled && (
                        <div className="space-y-2">
                          {/* Account Search */}
                          <input
                            type="text"
                            placeholder="Search accounts..."
                            value={accountSearchTerm}
                            onChange={(e) => setAccountSearchTerm(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                          />

                          <select
                            value={jeMassUpdateAccountId}
                            onChange={(e) => setJeMassUpdateAccountId(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                            size={6}
                          >
                            <option value="">-- Select Account --</option>
                            {Object.entries(accountGroups).map(([groupName, accounts]) => (
                              <optgroup key={groupName} label={groupName}>
                                {accounts
                                  .filter(acct => !accountSearchTerm || acct.name.toLowerCase().includes(accountSearchTerm.toLowerCase()))
                                  .map(acct => (
                                    <option key={acct.id} value={acct.id}>
                                      {acct.name}
                                    </option>
                                  ))}
                              </optgroup>
                            ))}
                          </select>

                          <button
                            onClick={handleJeMassUpdate}
                            disabled={!jeMassUpdateAccountId || Object.values(selectedJeLines).filter(Boolean).length === 0}
                            className="w-full px-3 py-2 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Apply to Selected
                          </button>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Apply Updates Button - hide after JE is created */}
                    {paymentLinks.length === 0 && (
                    <button
                      onClick={handleApplyJeUpdates}
                      disabled={getPendingJeUpdates().length === 0 || isApplyingJeUpdates || isLoading}
                      className="w-full px-4 py-2.5 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isApplyingJeUpdates ? 'Applying Updates...' : `Apply ${getPendingJeUpdates().length} Update(s)`}
                    </button>
                    )}

                    {/* Create JE Button - Only show when JE lines exist and no JE created yet */}
                    {paymentLinks.length === 0 && (
                      <button
                        onClick={handleCreateJE}
                        disabled={isLoading}
                        className="w-full mt-3 px-4 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Creating...
                          </>
                        ) : (
                          <>
                            ✨ Create Journal Entry
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* ======================== AR/AP AGENT PANEL ======================== */
              <>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                  📋 Transaction Status
                </h3>

                <div className="space-y-4">
                  {/* Enable Reallocation Checkbox - Hide when payment already created */}
                  {/* Also hide for bill_agent (vendor payments) - billlist is already mapped from NetSuite RESTlet */}
                  {openDocs.length > 0 && paymentLinks.length === 0 && transaction.agent_type !== 'bill_agent' && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={enableReallocation}
                        onChange={(e) => {
                          setEnableReallocation(e.target.checked);
                          if (!e.target.checked) setAllocationAmounts({});
                        }}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      Enable Reallocation
                    </label>
                  )}

                  {/* Transaction Details */}
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Predicted {entityNoun}:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{getPredictedName()}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Matched in NetSuite:</span>{' '}
                      {isMatched() ? (
                        <span className="text-green-600">✅ {getMatchedName()}</span>
                      ) : (
                        <span className="text-red-600">❌ Not matched</span>
                      )}
                    </div>
                    {/* TDS Section - Only for Customer Payments (bank_agent), NOT for vendor payments (bill_agent) */}
                    {transaction.agent_type === 'bank_agent' && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2 space-y-2">
                        {/* TDS Not Applicable Checkbox */}
                        <label className={`flex items-center gap-2 ${(tdsPaymentCreated || transaction.ns_payment_id?.length > 0) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={tdsNotApplicable}
                            disabled={tdsPaymentCreated || transaction.ns_payment_id?.length > 0}
                            onChange={(e) => {
                              setTdsNotApplicable(e.target.checked);
                              if (e.target.checked) {
                                setTdsPercentage('');
                                setTdsAmount(0);
                              } else if (transaction.ns_tds_percentage) {
                                setTdsPercentage(transaction.ns_tds_percentage);
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">TDS Not Applicable</span>
                          {(tdsPaymentCreated || transaction.ns_payment_id?.length > 0) && (
                            <span className="text-xs text-gray-400 ml-1">(Locked)</span>
                          )}
                        </label>

                        {/* TDS % and Amount - Only show when TDS is applicable */}
                        {!tdsNotApplicable && (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-700 dark:text-gray-300">TDS %:</span>
                              <input
                                type="text"
                                value={tdsPercentage}
                                onChange={(e) => setTdsPercentage(e.target.value)}
                                disabled={tdsPaymentCreated || transaction.ns_payment_id?.length > 0}
                                placeholder="Enter TDS %"
                                className={`w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${(tdsPaymentCreated || transaction.ns_payment_id?.length > 0) ? 'cursor-not-allowed opacity-60' : ''}`}
                              />
                              {(tdsPaymentCreated || transaction.ns_payment_id?.length > 0) && (
                                <span className="text-xs text-gray-400">(Locked)</span>
                              )}
                              {!(tdsPaymentCreated || transaction.ns_payment_id?.length > 0) && transaction.ns_tds_percentage && tdsPercentage !== transaction.ns_tds_percentage && (
                                <span className="text-xs text-amber-500">(Modified)</span>
                              )}
                            </div>
                            {tdsAmount > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">TDS Amount:</span>
                                <span className="text-green-600 font-medium">{formatAmount(tdsAmount)}</span>
                              </div>
                            )}
                          </>
                        )}

                      </div>
                    )}
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Amount:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{formatAmount(transaction.Amount)}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Date:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{transaction.trandate}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Bank:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{transaction.bank}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Description:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400 break-words">{transaction.description}</span>
                    </div>
                  </div>

                  {/* Entity Selection (when NOT matched) */}
                  {!isMatched() && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Select Correct {entityNoun}:
                      </div>

                      {/* Loading state */}
                      {isLoadingEntities && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin"></div>
                          Loading {entityNoun.toLowerCase()}s...
                        </div>
                      )}

                      {/* Dropdown with Search - use similarEntities if available, otherwise allEntities */}
                      {!isLoadingEntities && (
                        <>
                          {/* Search Input */}
                          <input
                            type="text"
                            placeholder={`Search ${entityNoun.toLowerCase()}s...`}
                            value={entitySearchTerm}
                            onChange={(e) => setEntitySearchTerm(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 mb-2"
                          />

                          <select
                            value={selectedEntityId}
                            onChange={(e) => setSelectedEntityId(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                            size={Math.min(8, (similarEntities.length > 0 ? similarEntities : allEntities).filter(opt =>
                              !entitySearchTerm || opt.label?.toLowerCase().includes(entitySearchTerm.toLowerCase())
                            ).length + 1)}
                          >
                            <option value="">-- Select {entityNoun} --</option>
                            {/* Filter and show entities based on search term */}
                            {(similarEntities.length > 0 ? similarEntities : allEntities)
                              .filter(opt => !entitySearchTerm || opt.label?.toLowerCase().includes(entitySearchTerm.toLowerCase()))
                              .map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                          </select>

                          {/* Show count info */}
                          {similarEntities.length === 0 && allEntities.length > 0 && (
                            <div className="text-xs text-gray-400 mt-1">
                              {entitySearchTerm
                                ? `Found ${allEntities.filter(e => e.label?.toLowerCase().includes(entitySearchTerm.toLowerCase())).length} of ${allEntities.length}`
                                : `Showing all ${allEntities.length} ${entityNoun.toLowerCase()}s`}
                            </div>
                          )}
                          {similarEntities.length > 0 && (
                            <div className="text-xs text-gray-400 mt-1">
                              {similarEntities.length} similar {entityNoun.toLowerCase()}s found
                            </div>
                          )}

                          <button
                            onClick={handleEntityUpdate}
                            disabled={!selectedEntityId || isUpdatingEntity || isLoading}
                            className="mt-2 w-full px-4 py-2 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isUpdatingEntity ? 'Updating...' : `Update ${entityNoun}`}
                          </button>

                          {/* Transfer to JE Button - Hide if transaction already has payment */}
                          {paymentLinks.length === 0 && (
                            <button
                              onClick={handleTransferToJE}
                              disabled={isTransferringToJE || isLoading}
                              className="mt-2 w-full px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTransferringToJE ? 'Transferring...' : 'Transfer to JE Agent'}
                            </button>
                          )}

                        </>
                      )}
                    </div>
                  )}

                  {/* Open Invoices/Bills/PO Details */}
                  {openDocs.length > 0 && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {console.log('🧾 [OPEN DOCS] nsrecord_type:', transaction.nsrecord_type, 'billlist:', transaction.billlist?.[0]?.nsrecord_type)}
                        🧾 {(transaction.nsrecord_type === 'vendorPrepayment' || transaction.billlist?.[0]?.nsrecord_type === 'vendorPrepayment') ? 'PO Details' : `Open ${docNoun}`}:
                      </div>
                      <div className="space-y-2">
                        {openDocs.map((doc, idx) => {
                          const isCredit = parseFloat(doc.amount) < 0;
                          return (
                          <div key={idx} className="border-b border-gray-100 dark:border-gray-700 pb-2 mb-2 last:border-0 last:pb-0 last:mb-0">
                            <div className="flex items-center gap-2 text-sm">
                              <div className="flex-1 text-gray-600 dark:text-gray-400">
                                <span className="font-medium">{doc.number}</span>
                                {isCredit && <span className="ml-1 text-xs text-green-600 font-semibold">(Credit)</span>}
                                {' — '}
                                <span className={isCredit ? 'text-green-600' : ''}>{formatAmount(doc.amount)}</span>
                                {doc.duedate && ` (Due: ${doc.duedate})`}
                              </div>
                              {enableReallocation && paymentLinks.length === 0 && (
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  value={allocationAmounts[doc.number] || ''}
                                  onChange={(e) => {
                                    let value = parseFloat(e.target.value) || 0;
                                    const maxDocAmt = Math.abs(parseFloat(doc.amount) || 0);
                                    const transactionAmount = Math.abs(parseFloat(transaction.Amount) || 0);

                                    // Check if value exceeds the document amount
                                    if (value > maxDocAmt) {
                                      alert(`Cannot allocate more than ${formatAmount(maxDocAmt)} for ${doc.number}`);
                                      value = maxDocAmt;
                                    }

                                    // Check if total allocation exceeds transaction amount
                                    let totalOthers = 0;
                                    Object.entries(allocationAmounts).forEach(([docNum, amt]) => {
                                      if (docNum !== doc.number && amt) {
                                        totalOthers += Math.abs(parseFloat(amt) || 0);
                                      }
                                    });
                                    const allowed = Math.max(transactionAmount - totalOthers, 0);
                                    if (value > allowed) {
                                      alert(`Total allocation exceeds payment amount ${formatAmount(transactionAmount)}. Adjusted ${doc.number} to ${formatAmount(allowed)}`);
                                      value = allowed;
                                    }

                                    setAllocationAmounts(prev => ({
                                      ...prev,
                                      [doc.number]: value || ''
                                    }));
                                  }}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                />
                              )}
                            </div>
                            {/* Show TDS breakdown for invoices when TDS is applicable */}
                            {transaction.agent_type === 'bank_agent' && !tdsNotApplicable && doc.totpayamount > 0 && (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-500 pl-2 flex gap-3">
                                <span>Item: {formatAmount(doc.totpayamount)}</span>
                                <span>Tax: {formatAmount(doc.taxtotal)}</span>
                                <span>Total: {formatAmount(doc.originalTotal)}</span>
                              </div>
                            )}
                          </div>
                        );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Suggested/Current Allocation with TDS */}
                  {(tdsAllocation?.allocations?.length > 0 || suggestedAlloc.length > 0) && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        {transaction.manual_allocations?.length > 0 ? (
                          <>
                            <span className="text-green-600">✏️ Current Allocation (Modified):</span>
                          </>
                        ) : transaction.agent_type === 'bill_agent' ? (
                          '💳 Payment Details:'
                        ) : (
                          `Suggested Allocation${!tdsNotApplicable && tdsPercentage ? ` (TDS @${tdsPercentage}%)` : ''}:`
                        )}
                      </div>

                      {/* Use allocation table if available */}
                      {tdsAllocation?.allocations?.length > 0 ? (
                        <>
                          {/* Allocation Table - Works for both TDS and non-TDS */}
                          <div className="space-y-1">
                            {tdsAllocation.allocations.map((alloc, idx) => (
                              <div key={idx} className="text-sm border-b border-gray-100 dark:border-gray-700 pb-2 mb-2 last:border-0">
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                  <span className="font-medium">{alloc.invoice_number} {alloc.is_partial && <span className="text-amber-500">(Partial)</span>}</span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                  <span>Due:</span>
                                  <span>{formatAmount(alloc.invoice_due)}</span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                  <span>Item Amount:</span>
                                  <span>{formatAmount(alloc.totpayamount)}</span>
                                </div>
                                <div className="flex justify-between text-xs text-blue-600 mt-0.5">
                                  <span>Apply:</span>
                                  <span>{formatAmount(alloc.amount_applied)}</span>
                                </div>
                                {/* Show Cash/TDS breakdown only if TDS is applicable */}
                                {!tdsAllocation.tds_not_applicable && alloc.tds_amount > 0 && (
                                  <>
                                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                      <span>Cash:</span>
                                      <span>{formatAmount(alloc.payment_allocated)}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-green-600 mt-0.5">
                                      <span>TDS JE:</span>
                                      <span>{formatAmount(alloc.tds_amount)}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                          {/* Summary - Shows different info based on TDS applicable or not */}
                          <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                            {!tdsAllocation.tds_not_applicable && tdsAllocation.total_tds > 0 ? (
                              <>
                                {/* TDS Summary */}
                                <div className="flex justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                                  <span>Total Cash Payment:</span>
                                  <span>{formatAmount(tdsAllocation.allocations.reduce((sum, a) => sum + (a.payment_allocated || 0), 0))}</span>
                                </div>
                                <div className="flex justify-between text-sm font-medium text-green-600">
                                  <span>Total TDS (JE Credits):</span>
                                  <span>{formatAmount(tdsAllocation.allocations.reduce((sum, a) => sum + (a.tds_amount || 0), 0))}</span>
                                </div>
                                <div className="flex justify-between text-sm font-bold text-gray-800 dark:text-gray-200 mt-1">
                                  <span>Total Applied to Invoices:</span>
                                  <span>{formatAmount(tdsAllocation.allocations.reduce((sum, a) => sum + (a.payment_allocated || 0) + (a.tds_amount || 0), 0))}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Non-TDS Summary */}
                                <div className="flex justify-between text-sm font-bold text-gray-800 dark:text-gray-200">
                                  <span>Total Applied to Invoices:</span>
                                  <span>{formatAmount(tdsAllocation.allocations.reduce((sum, a) => sum + (a.amount_applied || 0), 0))}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        /* Fallback to simple suggested allocation */
                        suggestedAlloc.map((s, idx) => (
                          <div key={idx} className={`text-sm ${s.isManual ? 'text-green-600 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                            {s.number} — {formatAmount(s.amount)}
                          </div>
                        ))
                      )}

                      {/* Create Payment Button - Only show when allocation exists and no payment created yet */}
                      {paymentLinks.length === 0 && !tdsPaymentCreated && isMatched() && (
                        <div className="mt-3">
                          <button
                            onClick={handleCreatePayment}
                            disabled={loadingAction !== null}
                            className="w-full px-4 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {loadingAction === 'create' ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Creating...
                              </>
                            ) : (
                              <>
                                ✨ Create {transaction.agent_type === 'bill_agent'
                                  ? ((transaction.nsrecord_type === 'vendorPrepayment' || transaction.billlist?.[0]?.nsrecord_type === 'vendorPrepayment') ? 'Vendor Prepayment' : 'Vendor Payment')
                                  : 'Customer Payment'}
                              </>
                            )}
                          </button>
                          {/* Check Created Payment Button */}
                          <button
                            onClick={handleCheckPayment}
                            disabled={loadingAction !== null}
                            className="w-full mt-2 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {loadingAction === 'check' ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Checking...
                              </>
                            ) : (
                              <>
                                🔍 Check Created Payment
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Created - Show payment info (works for TDS, vendor, and customer payments) */}
                  {tdsPaymentCreated && console.log('🔍 RENDER DEBUG:', {
                    tdsPaymentCreated,
                    transaction_nsrecord_type: transaction.nsrecord_type,
                    transaction_billlist: transaction.billlist,
                    transaction_nsbilldocnum: transaction.nsbilldocnum
                  })}
                  {tdsPaymentCreated && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        ✅ {transaction.agent_type === 'bill_agent'
                          ? ((transaction.nsrecord_type === 'vendorPrepayment' || transaction.billlist?.[0]?.nsrecord_type === 'vendorPrepayment') ? 'Vendor Prepayment' : 'Bill Payment')
                          : transaction.agent_type === 'je_agent' ? 'Journal Entry' : 'Payment'} Created:
                      </div>
                      {/* Show payment links */}
                      {tdsPaymentCreated.all_payments && tdsPaymentCreated.all_payments.length > 0 && (
                        <div className="mb-2">
                          {tdsPaymentCreated.all_payments.map((pmt, idx) => (
                            <div key={idx}>
                              <a
                                href={pmt.payment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                              >
                                View {transaction.agent_type === 'bill_agent'
                                  ? ((transaction.nsrecord_type === 'vendorPrepayment' || transaction.billlist?.[0]?.nsrecord_type === 'vendorPrepayment') ? 'Prepayment' : 'Bill Payment')
                                  : 'Payment'} {pmt.payment_doc}
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Show JE links (for TDS) */}
                      {tdsPaymentCreated.created_jes && tdsPaymentCreated.created_jes.length > 0 && (
                        <div className="mt-2">
                          {tdsPaymentCreated.created_jes.map((je, idx) => (
                            <div key={idx}>
                              <a
                                href={je.je_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                              >
                                View TDS JE {je.je_doc}
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Links */}
                  {paymentLinks.length > 0 && !tdsPaymentCreated && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        ✅ {transaction.agent_type === 'bill_agent'
                          ? ((transaction.nsrecord_type === 'vendorPrepayment' || transaction.billlist?.[0]?.nsrecord_type === 'vendorPrepayment') ? 'Vendor Prepayment' : 'Bill Payment')
                          : transaction.agent_type === 'je_agent' ? 'Journal Entry' : 'Customer Payment'}:
                      </div>
                      {paymentLinks.map((link) => (
                        <div key={link.id}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            View {link.label}
                          </a>
                        </div>
                      ))}
                      {/* Check Created Payment Button - verify payment in NetSuite */}
                      <button
                        onClick={handleCheckPayment}
                        disabled={loadingAction !== null}
                        className="w-full mt-3 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {loadingAction === 'check' ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Checking...
                          </>
                        ) : (
                          <>
                            🔍 Check Created Payment
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Reallocate Button - Hide when payment already created */}
                  {openDocs.length > 0 && paymentLinks.length === 0 && (
                    <div className="pt-3">
                      <button
                        onClick={handleReallocation}
                        disabled={!isAnyAllocationValid || isUpdatingAllocation || isLoading}
                        className="w-full px-4 py-2.5 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUpdatingAllocation ? 'Processing...' : `Reallocate ${docNoun}`}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default BankAgentChat;
